"use client";

/**
 * MuxVideoPlayerWithCTA - Estilo TikTok Shop (production-ready)
 *
 * Single, scalable component for multiple videos + CTA. The CTA (redirect URL,
 * label, icon) is per-page: each static page has its own setup via
 * staticPageVideoConfig + env keys (e.g. VITE_MEJORES_AMIGOS_*, VITE_TELEGRAM_GROUPS_*).
 * Funnel "one asset first": `VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID` (Mux ID or stream.mux.com URL)
 * feeds Telegram/channel_invite when per-locale IDs are unset; optional `VITE_MUX_CTA_FREE_JOIN_COPY`
 * switches CTA copy to "join free" (see videoCTAConfig). Cartel sutil encima del CTA:
 * `VITE_MUX_CTA_FRIENDLY_BANNER=true` o free-join; apagar: `VITE_MUX_CTA_FRIENDLY_BANNER=false` (y sin free-join).
 *
 * Add new pages by extending STATIC_PAGE_VIDEO_REGISTRY; no component changes.
 *
 * Usage:
 * - Static pages: `page` or pathname → config per page (video + CTA redirect).
 * - BioLinkTemplate modal replacement: use `variant="modal"` + config (playbackId, ctaUrl,
 *   ctaLabel, onClose, onCTAClick). One modal block, config-driven; any button can open it
 *   with its own config. You define which modal to replace when wiring the flow.
 * - Overlays / other: manual playbackId/ctaUrl or `service`.
 *
 * Config priority: manual > page > service. Con Cloudflare, `resolveWorkerDrivenCta()` puede
 * sustituir URL/label del CTA salvo `skipWorkerTemplateCta`.
 *
 * País para pricing / URLs por país: prop `countryCode` si viene del padre (Worker); si no,
 * `detectCountryCode()` (prioriza `getServerTemplateConfig()` → __TEMPLATE_CONFIG__ o cookies).
 */

import {
  getMuxCtaUniversalPlaybackId,
} from "@/config/muxCtaUniversalPlayback";
import {
  type StaticPageVideoPage,
  getStaticPageFromPathname,
  getStaticPageVideoConfig,
} from "@/config/staticPageVideoConfig";
import {
  CREATE_PAYMENT_INTENT_ENDPOINT,
  STRIPE_APPEARANCE,
  STRIPE_PUBLISHABLE_KEY,
  getPackPricing as getPackPricingStatic,
} from "@/config/stripe";
import {
  TELEGRAM_GROUPS_EN_PLAYBACK_ID,
  getTelegramCTAUrlByCountry,
  getTelegramPlaybackIdByCountry,
} from "@/config/telegramGroupsVideo";
import {
  type MuxFriendlyFunnelOpts,
  type SupportedLanguage,
  type VideoCTAService,
  getLocalizedLabel,
  getLocalizedTitle,
  getMuxCtaFriendlyBannerMessage,
  getVideoCTAConfigWithPayment,
} from "@/config/videoCTAConfig";
import { trackButtonClickGA } from "@/lib/analytics";
import {
  BRAND_SOURCE_KEY,
  BRAND_MERCADOPAGO_HANDSHAKE_URL,
} from "@/lib/brandConfig";
import { isArgentinaPaymentContext } from "@/lib/resolveCheckoutCountryCode";
import { getDataFastStripeMetadata } from "@/lib/datafastAnalytics";
import { fetchPlanPricing } from "@/lib/planPricingCache";
import {
  getMercadoPagoCheckoutBasicArUrl,
  getMercadoPagoCheckoutVipArUrl,
} from "@/lib/mercadopagoArCheckoutUrl";
import { useMercadoPagoInstagramEscape } from "@/hooks/useMercadoPagoInstagramEscape";
import type { PackConfig } from "@/lib/api";
import { detectCountryCode } from "@/lib/countryDetection";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import { coalesceMuxPlaybackId } from "@/lib/canonicalMuxPlayback";
import { resolveMuxCtaPlaybackFallback, readRuntimeBrandEnv } from "@/lib/runtimeBrandEnv";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { prefetchBrandRuntimeConfig } from "@/lib/brandRuntimeConfig";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import {
  getUserLanguage,
  usePackConfigForVideo,
} from "@/lib/packConfigHelpers";
import { formatAmountFromCents } from "@/lib/dualCtaPriceDisplay";
import { trackBirthdayCtaClick } from "@/lib/birthdayCtaAnalytics";
import { isCumpleDestination } from "@/lib/birthdayCtaAttribution";
import {
  trackMuxCheckoutClick,
  trackMuxCtaBlocked,
  trackMuxCtaViewed,
  trackMuxStripeRedirect,
} from "@/lib/muxPaywallFunnelTracking";
import { persistDualCtaCheckoutEmail } from "@/lib/dualCtaFunnelTracking";
import { trackButtonClickPostHog, trackStripePayFailurePostHog, flushPostHogBeforeNavigation } from "@/lib/posthogAnalytics";
import { MUX_CTA_ACTIVE_EVENT } from "@/lib/consentSurfaces";
import {
  computeStripePackCheckoutAmounts,
  getOrCreateSessionWatcherLuckPercent,
  getWatcherLuckDiscountMaxPercentClient,
} from "@/lib/watcherLuckDiscount";
import { usePackConfig } from "@/lib/usePackConfig";
import { useVideoAbSlot } from "@/lib/useVideoAbSlot";
import {
  getWorkerCtaDisplayLabel,
  resolveWorkerDrivenCta,
} from "@/lib/workerDrivenCta";
import MuxPlayer from "@mux/mux-player-react";
import type { HlsConfig } from "hls.js";
import { Elements } from "@stripe/react-stripe-js";
import { type Stripe, loadStripe } from "@stripe/stripe-js";
import { ExternalLink, Instagram, Loader2, Play, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { StripeCheckoutForm } from "./StripeCheckoutForm";

const SAFE_PLAYBACK_ID_FALLBACK = "mux-cta-default";

/**
 * Mux Data environment key (Mux dashboard → Settings → Data → Env key). When set, the player
 * reports views to your Mux Data dashboard so they're attributable. Build-time, client-safe.
 * Leave unset → Mux Player still auto-reports to the asset's own Data environment.
 */
const MUX_DATA_ENV_KEY =
  (import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined)?.trim() || undefined;
/** Fallback derivado del config compartido — sin literales del fork anterior.
 *  Si la env del brand actual está vacía, el resolver downstream devuelve null
 *  y el componente degrada graciosamente (CTAs visibles, sin video). */
const HARDCODED_FALLBACK_PLAYBACK_ID =
  readRuntimeBrandEnv("VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID") ||
  readRuntimeBrandEnv("VITE_PREMIUM_MUX_ES_VIP") ||
  readRuntimeBrandEnv("VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID") ||
  "";

/** "Suerte" toast: no mostrar al iniciar play; solo tras contexto de vista real o fallback de tiempo. */
const WATCHER_LUCK_MIN_PLAYHEAD_SEC = 8.25;
const WATCHER_LUCK_FALLBACK_WALL_MS = 17_000;
const WATCHER_LUCK_TOAST_MS = 5_400;

/** Superficie de embudo activa (p. ej. dual_cta_video_landing) — inyectada en errores Mux. */
let muxPlayerAnalyticsFlowSurface: string | undefined;

/**
 * Log para producción: trackea todos los eventos de video y errores de red
 * Envía a PostHog (mux_video_event) + console.error para debugging en prod.
 */
function logVideoEvent(payload: {
  event:
    | "video_error"
    | "video_load_start"
    | "video_load_success"
    | "video_load_failed"
    | "video_play_attempt"
    | "video_play_success"
    | "video_play_failed"
    | "video_stalled"
    | "video_buffering"
    | "network_error"
    | "playback_id_invalid"
    | "fallback_attempted"
    | "fallback_failed"
    | "video_shown_thumbnail_only";
  country?: string;
  service?: string;
  playbackId?: string;
  errorType?: string;
  errorMessage?: string;
  errorCode?: string | number;
  networkState?: number;
  readyState?: number;
  isNetworkError?: boolean;
  isTokenError?: boolean;
  fallbackAttempted?: boolean;
  userAgent?: string;
  connectionType?: string;
  duration?: number;
  currentTime?: number;
  /** Foreground-only ms spent waiting for first playback before the load-watchdog gave up. */
  foregroundTimeMs?: number;
  /** Wall-clock ms spent waiting (foreground + background). Diagnoses tab-switching cases. */
  wallTimeMs?: number;
  /** Whether the page was hidden at least once between mount and the watchdog firing. */
  wasHidden?: boolean;
  /** When set (e.g. dual_cta_video_landing), errores Mux disparan embudo DualCTA en PostHog. */
  analyticsFlowSurface?: string;
}) {
  const isMuxErrorEvent =
    payload.event.includes("error") || payload.event.includes("failed");
  const enrichedPayload =
    isMuxErrorEvent &&
    muxPlayerAnalyticsFlowSurface &&
    !payload.analyticsFlowSurface
      ? { ...payload, analyticsFlowSurface: muxPlayerAnalyticsFlowSurface }
      : payload;
  const data = {
    ...enrichedPayload,
    ts: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    // Network info si está disponible
    connectionType:
      (navigator as { connection?: { effectiveType?: string } }).connection
        ?.effectiveType || "unknown",
    connectionDownlink:
      (navigator as { connection?: { downlink?: number } }).connection
        ?.downlink || null,
    component: "MuxVideoPlayerWithCTA",
  };

  // Console: errores siempre (debugging útil en prod si DevTools abierto),
  // eventos normales solo en DEV — el JSON dump por evento (load_start,
  // play_success, buffering, etc.) arruinaba la consola en prod.
  const isErrorEvent = isMuxErrorEvent;
  if (isErrorEvent) {
    console.error("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
  } else if (import.meta.env.DEV) {
    console.log("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
  }

  // PostHog: solo eventos de alto valor — video_buffering/load_start inundan
  // el timeline del replay (ver sesiones DualCTA con 8+ mux_video_event seguidos).
  const POSTHOG_MUX_EVENTS = new Set([
    "video_error",
    "video_load_failed",
    "video_play_failed",
    "video_play_success",
    "video_load_success",
    "video_stalled",
    "network_error",
    "playback_id_invalid",
    "fallback_failed",
    "video_shown_thumbnail_only",
  ]);
  if (
    typeof window !== "undefined" &&
    POSTHOG_MUX_EVENTS.has(payload.event) &&
    (window as { posthog?: { capture: (e: string, p?: object) => void } })
      .posthog?.capture
  ) {
    (
      window as { posthog: { capture: (e: string, p?: object) => void } }
    ).posthog.capture("mux_video_event", { source: BRAND_SOURCE_KEY, ...data });
    // DualCTA funnel errors: el padre (DualCTAVideoLanding) trackea vía onError
    // solo en errores fatales / rotación — no duplicar mux_video_event aquí.
  }

  // 2026-07-16: removido el sender a Axiom — no se usa (cero vars AXIOM en
  // Railway; era dead code gateado por VITE_AXIOM_INGEST_ENDPOINT). La
  // observabilidad server-side es Datadog vía el forwarder propio de Railway
  // (server/datadogLogForwarder.ts): los errores fatales del DualCTA llegan
  // por /api/track-funnel-failure → console.error("[mux][error] …") → Datadog.
}

// Mantener compatibilidad con código existente
function logVideoErrorProduction(payload: Parameters<typeof logVideoEvent>[0]) {
  logVideoEvent(payload);
}

// Checkout flow states
type CheckoutState =
  | "VIDEO_PREVIEW"
  | "CHECKOUT"
  | "PROCESSING"
  | "SUCCESS"
  | "ERROR";

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

/** Sanitiza playbackId para uso en CSS/className (evita XSS). */
function sanitizePlaybackIdForCss(id: string): string {
  const safe = id.replace(/[^a-zA-Z0-9_-]/g, "");
  return safe.length > 0 ? safe : SAFE_PLAYBACK_ID_FALLBACK;
}

/** Valida que la URL sea http/https o relativa (rechaza javascript:, data:, etc.). */
function isAllowedRedirectUrl(url: string): boolean {
  // Allow relative URLs (starting with /)
  if (url.startsWith("/")) {
    return true;
  }

  // For absolute URLs, check protocol
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

/**
 * Detect iOS devices (iPhone, iPad, iPod)
 */
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPad with iOS 13+
    setIsIOS(isIOSDevice);
  }, []);

  return isIOS;
};

/**
 * Detect Android devices
 */
const useIsAndroid = () => {
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    setIsAndroid(/android/.test(userAgent));
  }, []);

  return isAndroid;
};

/**
 * Chromium desktop en macOS (Chrome, Brave, Arc, Opera…) y Arc explícito.
 * Arc a veces no incluye "Arc/" en el user agent; el síntoma típico es fallo MSE en el buffer
 * de audio (`SourceBuffer`/`appendBuffer`). Activamos el mismo endurecimiento para todo
 * Chrome en Mac (no Safari, no Edge).
 */
function detectMuxDesktopMacMseHardening(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/\bArc\/\d/i.test(ua)) return true;
  const brands = (
    navigator as Navigator & {
      userAgentData?: { brands?: { brand: string }[] };
    }
  ).userAgentData?.brands;
  if (brands?.some((b) => b.brand === "Arc")) return true;

  const mac = /Mac OS X|Macintosh/.test(ua);
  const chromiumNotEdge = /\bChrome\/\d/.test(ua) && !/\bEdg\/\d/.test(ua);

  return mac && chromiumNotEdge;
}

const useMuxDesktopMacMseHardening = () =>
  useMemo(() => detectMuxDesktopMacMseHardening(), []);

/**
 * Detect if device is in landscape orientation
 */
const useIsLandscape = () => {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkOrientation = () => {
      // Use screen.orientation if available, fallback to window dimensions
      if (screen.orientation) {
        setIsLandscape(screen.orientation.type.includes("landscape"));
      } else {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  return isLandscape;
};

/**
 * Config for using MuxVideoPlayerWithCTA as overlay in BioLinkTemplate (fullscreen reel or modal).
 * Pass variant + these props + onClose. "fullscreen" = reel-style full viewport; "modal" = centered panel.
 */
export type VideoCTAModalConfig = Pick<
  MuxVideoPlayerWithCTAProps,
  | "service"
  | "playbackId"
  | "ctaUrl"
  | "ctaLabel"
  | "title"
  | "openInNewTab"
  | "displayMode"
  | "loop"
  | "playbackIdByCountry"
  | "ctaUrlByCountry"
  | "countryCode"
  | "language"
  | "skipWorkerTemplateCta"
> & {
  onCTAClick?: (url: string) => void;
  /** "fullscreen" = reel full viewport (default); "modal" = centered panel. */
  variant?: "fullscreen" | "modal";
};

/**
 * Telemetría ligera de posición de reproducción (BioTemplatePremium / funnel).
 * `timeupdate` está limitada a ~1 evento / 4s para no saturar PostHog.
 */
export type PlaybackTelemetryPayload = {
  currentTimeSeconds: number;
  durationSeconds: number | null;
  playbackId: string;
  reason: "timeupdate" | "pause" | "ended";
};

export interface MuxVideoPlayerWithCTAProps {
  // Option 1: Use service type for automatic configuration
  service?: VideoCTAService;
  countryCode?: string;
  language?: SupportedLanguage;

  // Option 2: Static page – video + CTA per page. CTA redirect/label/icon come from
  // staticPageVideoConfig (registry + env). Set per page according to needs.
  page?: StaticPageVideoPage;

  // Option 3: Manual configuration (overrides service/page config if provided)
  playbackId?: string;
  /**
   * Si true, el playbackId manual tiene prioridad absoluta y NO se usan fallbacks por país.
   * Útil para templates con videos específicos (e.g. BioTemplatePremium).
   */
  forceManualPlaybackId?: boolean;
  title?: string;
  ctaUrl?: string; // Redirect al hacer click en "Unirte"
  ctaLabel?: string; // default: "Unirte"
  /** Texto opcional arriba del botón CTA (ej. "Ingresar al Canal Privado + Hablar conmigo") */
  ctaSubtitle?: string;
  /**
   * Overlay VIP en BioTemplatePremiumSimple: cartel "no es VIP" cuando el CTA abre Telegram gratis
   * (`premiumSimpleMembershipFreeFunnel` + `getMuxCtaFriendlyBannerMessage`).
   */
  premiumSimpleMembershipFreeFunnel?: boolean;
  /** Resolución admin + env para el cartel "no es VIP" (Invitación premium en DB). */
  muxFriendlyFunnel?: MuxFriendlyFunnelOpts | null;
  openInNewTab?: boolean;

  // Option 4: Country-specific video configurations
  // Supports per-country videos for different languages/regions
  playbackIdByCountry?: {
    // North America
    US?: string;

    // Europe
    ES?: string; // Spain
    DE?: string; // Germany
    FR?: string; // France

    // LATAM (BioLinkTemplate countries)
    MX?: string; // Mexico
    CL?: string; // Chile
    CO?: string; // Colombia
    PE?: string; // Peru
    PY?: string; // Paraguay
    AR?: string; // Argentina
    BR?: string; // Brazil

    // Middle East
    AE?: string; // UAE (United Arab Emirates / Emiratos)

    // Fallback for other countries
    default?: string;
  };

  // Option 5: Country-specific CTA URL configurations (Stripe Payment Links)
  // Different pricing per country - managed via Vercel env vars
  ctaUrlByCountry?: {
    // North America
    US?: string; // e.g. $14.99 USD

    // Europe
    ES?: string; // e.g. €12.99 EUR (Spain)
    DE?: string; // e.g. €14.99 EUR (Germany)
    FR?: string; // e.g. €13.99 EUR (France)

    // LATAM (BioLinkTemplate countries) - Each country can have different pricing
    MX?: string; // e.g. $199 MXN (Mexico)
    CL?: string; // e.g. CLP pricing (Chile)
    CO?: string; // e.g. COP pricing (Colombia)
    PE?: string; // e.g. PEN pricing (Peru)
    PY?: string; // e.g. PYG pricing (Paraguay)
    AR?: string; // e.g. ARS pricing (Argentina)
    BR?: string; // e.g. R$49 BRL (Brazil)

    // Middle East
    AE?: string; // e.g. AED 49 (UAE / Emiratos)

    // Fallback for other countries (generic LATAM pricing)
    default?: string;
  };

  displayMode?: "reel" | "contain" | "cover" | "auto";
  loop?: boolean;
  
  /** 
   * Control de autoplay. Por defecto "any" (intenta con sonido, fallback muted).
   * - "any": autoplay con sonido, fallback muted (TikTok/Reels style)
   * - "muted": autoplay solo muted
   * - false: no autoplay, requiere click del usuario
   */
  autoPlay?: "any" | "muted" | false;

  /** 'fullscreen' = fill container (default). 'modal' = backdrop + centered rounded panel, Escape/backdrop to close. */
  variant?: "fullscreen" | "modal";

  /** When set, custom checkout uses this pack (e.g. exclusive, sorpresa) for pricing and payment instead of channel_invite. */
  overridePackId?: string;

  /** Si true, no se muestra el aviso "Toca para escuchar". Solo FullscreenExclusive (/exclusive) debe pasarlo; otras rutas no. */
  hideUnmutePrompt?: boolean;

  /**
   * Intenta autoplay con sonido incluso en Instagram IAB (no fuerza muted en el
   * elemento <video>). Si el navegador bloquea audio sin gesto, cae a muted +
   * aviso "Toca para escuchar".
   */
  preferAutoplayWithSound?: boolean;

  /**
   * Si true, no aplica el CTA inyectado por Cloudflare (`window.__TEMPLATE_CONFIG__`).
   * Por defecto false: `hideCta` vacía el CTA; `showCommunity` / `showBirthday` / `showWishlist` sustituyen URL y texto.
   */
  skipWorkerTemplateCta?: boolean;

  /**
   * El checkout lo maneja el padre (botones VIP/Free fuera del player, cards en BioTemplate).
   * Evita `mux_cta_blocked` / "Sin URL ni pack" en /admin/mux-conversion.
   */
  externalCheckoutCta?: boolean;

  onReady?: () => void;
  onPlay?: () => void;
  /** Fires when the first frame is actually rendering (`playing`), not merely `play`. */
  onPlaying?: () => void;
  onEnded?: () => void;
  /**
   * Alternate Mux playback IDs to try after decode/network errors when `playbackId`
   * is manual (e.g. DualCTAVideoLanding teaser rotation).
   */
  fallbackPlaybackIds?: string[];
  /**
   * When true, skip internal playback-id rotation — delegate to parent `onError`
   * (DualCTAVideoLanding remounts via playerRemountKey + pickNextPlaybackId).
   */
  parentOwnsPlaybackRotation?: boolean;
  onCTAClick?: (url: string) => void;
  onClose?: () => void;
  /** Fired throttled (~4s) on timeupdate, and always on pause/ended — para métricas de cuánto vio el video. */
  onPlaybackTelemetry?: (payload: PlaybackTelemetryPayload) => void;
  /**
   * Fired only when playback is unrecoverable for the user (retries/fallback
   * exhausted, or blocking error UI). Transient Mux/HLS errors that recover via
   * silent reload or playback-id rotation do NOT call this.
   */
  onError?: (message: string) => void;

  /**
   * Mux Data video_title override (analytics only — does NOT render visible UI, unlike `title`).
   * Use to attribute views to a consistent label (e.g. the teaser tag "teaserdelfu").
   */
  muxDataVideoTitle?: string;
  /** Optional Mux Data video_series for grouping views in the Data dashboard. */
  muxDataVideoSeries?: string;

  /**
   * Optional A/B slot key (e.g. "dual_cta_landing"). When provided, resolves a
   * playback ID from the admin-managed video_ab_slots table (session-stable).
   * Takes priority over `playbackId` prop when the slot has enabled variants.
   */
  slotKey?: string;
  className?: string;
  containerClassName?: string;
  /** Force full mobile viewport height in cover mode (e.g. DualCTA landing). */
  fullViewportOnMobile?: boolean;
  /**
   * Superficie PostHog para errores Mux (p. ej. dual_cta_video_landing).
   * Dispara dual_cta_mux_error_no_conversion cuando el visitante no convirtió.
   */
  analyticsFlowSurface?: string;

  /**
   * Modo “preview del padre”: suprime shimmer, heavy/light loaders, thumbnails
   * LQIP internos y poster del <mux-player>. El player queda invisible hasta el
   * primer frame real (`playing`) para que DualCTAVideoLanding (u otro padre)
   * controle el poster + spinner sin overlap ni flash de otro asset.
   */
  hideLoadingShimmer?: boolean;
}

// Translation helper for UI texts
type TranslationKey =
  | "preparingCheckout"
  | "openingCheckoutPage"
  | "paymentError"
  | "paymentErrorMsg"
  | "retry"
  | "retryLoading"
  | "back"
  | "enableAudio"
  | "tapForSound"
  | "tapToPlayWithSound"
  | "openInBrowser"
  | "betterExperience"
  | "linkCopied"
  | "pasteInBrowser"
  | "longPressHint"
  | "loading"
  | "loadingVideo"
  | "connectingToPlayer"
  | "inAppBrowserVideoLoadHint"
  | "inAppBrowserMenuHint"
  | "startingPlayback"
  | "slowConnection"
  | "optimizingStream"
  | "decodingPreview"
  | "playbackReadyBrief";

const translations: Record<
  TranslationKey,
  Record<SupportedLanguage, string>
> = {
  preparingCheckout: {
    en: "Preparing checkout...",
    es: "Preparando checkout...",
    de: "Checkout wird vorbereitet...",
    fr: "Préparation du paiement...",
    it: "Preparazione checkout...",
    pt: "Preparando checkout...",
    nl: "Afrekenen voorbereiden...",
    tr: "Ödeme hazırlanıyor...",
    ar: "جارٍ تحضير الدفع...",
    pl: "Przygotowywanie płatności...",
    ja: "チェックアウトを準備中...",
    ko: "결제 준비 중...",
    id: "Mempersiapkan checkout...",
  },
  openingCheckoutPage: {
    en: "Opening checkout — please wait…",
    es: "Abriendo el checkout — esperá un momento…",
    de: "Checkout wird geöffnet — bitte warten…",
    fr: "Ouverture du paiement — patientez…",
    it: "Apertura del checkout — attendere…",
    pt: "Abrindo o checkout — aguarde…",
    nl: "Checkout openen — even geduld…",
    tr: "Ödeme sayfası açılıyor — lütfen bekleyin…",
    ar: "فتح صفحة الدفع — يرجى الانتظار…",
    pl: "Otwieranie płatności — proszę czekać…",
    ja: "チェックアウトを開いています — お待ちください…",
    ko: "결제 페이지를 여는 중 — 잠시 기다려주세요…",
    id: "Membuka checkout — harap tunggu…",
  },
  paymentError: {
    en: "Payment error",
    es: "Error en el pago",
    de: "Zahlungsfehler",
    fr: "Erreur de paiement",
    it: "Errore di pagamento",
    pt: "Erro no pagamento",
    nl: "Betaalfout",
    tr: "Ödeme hatası",
    ar: "خطأ في الدفع",
    pl: "Błąd płatności",
    ja: "支払いエラー",
    ko: "결제 오류",
    id: "Kesalahan pembayaran",
  },
  paymentErrorMsg: {
    en: "Something went wrong. Please try again.",
    es: "Hubo un problema al procesar tu pago. Por favor, intenta de nuevo.",
    de: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
    fr: "Quelque chose s'est mal passé. Veuillez réessayer.",
    it: "Si è verificato un problema. Per favore riprova.",
    pt: "Algo deu errado. Por favor, tente novamente.",
    nl: "Er is iets misgegaan. Probeer het opnieuw.",
    tr: "Bir şeyler ters gitti. Lütfen tekrar deneyin.",
    ar: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    pl: "Coś poszło nie tak. Spróbuj ponownie.",
    ja: "問題が発生しました。もう一度お試しください。",
    ko: "문제가 발생했습니다. 다시 시도해주세요.",
    id: "Ada yang tidak beres. Silakan coba lagi.",
  },
  retry: {
    en: "Try again",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    it: "Riprova",
    pt: "Tentar novamente",
    nl: "Opnieuw proberen",
    tr: "Tekrar dene",
    ar: "حاول مجدداً",
    pl: "Spróbuj ponownie",
    ja: "もう一度試す",
    ko: "다시 시도",
    id: "Coba lagi",
  },
  retryLoading: {
    en: "Retry loading",
    es: "Reintentar carga",
    de: "Laden erneut versuchen",
    fr: "Relancer le chargement",
    it: "Riprova il caricamento",
    pt: "Tentar carregar novamente",
    nl: "Opnieuw laden",
    tr: "Yüklemeyi yeniden dene",
    ar: "إعادة المحاولة",
    pl: "Ponów ładowanie",
    ja: "再読み込み",
    ko: "다시 로드",
    id: "Coba muat ulang",
  },
  back: {
    en: "Back",
    es: "Volver",
    de: "Zurück",
    fr: "Retour",
    it: "Indietro",
    pt: "Voltar",
    nl: "Terug",
    tr: "Geri",
    ar: "رجوع",
    pl: "Wróć",
    ja: "戻る",
    ko: "뒤로",
    id: "Kembali",
  },
  enableAudio: {
    en: "Enable audio",
    es: "Activar audio",
    de: "Audio aktivieren",
    fr: "Activer l'audio",
    it: "Attiva audio",
    pt: "Ativar áudio",
    nl: "Audio inschakelen",
    tr: "Sesi etkinleştir",
    ar: "تفعيل الصوت",
    pl: "Włącz dźwięk",
    ja: "音声を有効にする",
    ko: "오디오 활성화",
    id: "Aktifkan audio",
  },
  tapForSound: {
    en: "🔊 Tap for sound",
    es: "🔊 Toca para escuchar",
    de: "🔊 Tippen für Ton",
    fr: "🔊 Toucher pour le son",
    it: "🔊 Tocca per l'audio",
    pt: "🔊 Toque para o som",
    nl: "🔊 Tik voor geluid",
    tr: "🔊 Ses için dokun",
    ar: "🔊 اضغط للصوت",
    pl: "🔊 Dotknij dla dźwięku",
    ja: "🔊 タップして音声を聞く",
    ko: "🔊 소리를 위해 탭하세요",
    id: "🔊 Ketuk untuk suara",
  },
  tapToPlayWithSound: {
    en: "🔊 Tap to play with sound",
    es: "🔊 Toca para reproducir con audio",
    de: "🔊 Tippen um mit Ton abzuspielen",
    fr: "🔊 Toucher pour lire avec le son",
    it: "🔊 Tocca per riprodurre con audio",
    pt: "🔊 Toque para reproduzir com som",
    nl: "🔊 Tik om af te spelen met geluid",
    tr: "🔊 Sesli oynatmak için dokun",
    ar: "🔊 اضغط للتشغيل مع الصوت",
    pl: "🔊 Dotknij, aby odtwarzać z dźwiękiem",
    ja: "🔊 タップして音声付きで再生",
    ko: "🔊 소리와 함께 재생하려면 탭하세요",
    id: "🔊 Ketuk untuk memutar dengan suara",
  },
  openInBrowser: {
    en: "Open in Browser",
    es: "Abrir en Navegador",
    de: "Im Browser öffnen",
    fr: "Ouvrir dans le navigateur",
    it: "Apri nel browser",
    pt: "Abrir no navegador",
    nl: "Openen in browser",
    tr: "Tarayıcıda Aç",
    ar: "فتح في المتصفح",
    pl: "Otwórz w przeglądarce",
    ja: "ブラウザで開く",
    ko: "브라우저에서 열기",
    id: "Buka di Browser",
  },
  betterExperience: {
    en: "For the best experience, open this page in your browser",
    es: "Para una mejor experiencia, abre esta página en tu navegador",
    de: "Für die beste Erfahrung, öffnen Sie diese Seite in Ihrem Browser",
    fr: "Pour une meilleure expérience, ouvrez cette page dans votre navigateur",
    it: "Per un'esperienza migliore, apri questa pagina nel tuo browser",
    pt: "Para uma melhor experiência, abra esta página no seu navegador",
    nl: "Voor de beste ervaring, open deze pagina in uw browser",
    tr: "En iyi deneyim için bu sayfayı tarayıcınızda açın",
    ar: "للحصول على أفضل تجربة، افتح هذه الصفحة في متصفحك",
    pl: "Aby uzyskać najlepsze wrażenia, otwórz tę stronę w przeglądarce",
    ja: "最良の体験のために、このページをブラウザで開いてください",
    ko: "최상의 경험을 위해 이 페이지를 브라우저에서 열어주세요",
    id: "Untuk pengalaman terbaik, buka halaman ini di browser Anda",
  },
  linkCopied: {
    en: "Link copied!",
    es: "¡Enlace copiado!",
    de: "Link kopiert!",
    fr: "Lien copié !",
    it: "Link copiato!",
    pt: "Link copiado!",
    nl: "Link gekopieerd!",
    tr: "Bağlantı kopyalandı!",
    ar: "تم نسخ الرابط!",
    pl: "Link skopiowany!",
    ja: "リンクをコピーしました！",
    ko: "링크 복사됨!",
    id: "Tautan disalin!",
  },
  pasteInBrowser: {
    en: "Paste in Safari or Chrome to open",
    es: "Pega en Safari o Chrome para abrir",
    de: "In Safari oder Chrome einfügen zum Öffnen",
    fr: "Collez dans Safari ou Chrome pour ouvrir",
    it: "Incolla in Safari o Chrome per aprire",
    pt: "Cole no Safari ou Chrome para abrir",
    nl: "Plak in Safari of Chrome om te openen",
    tr: "Açmak için Safari veya Chrome'a yapıştırın",
    ar: "الصقه في Safari أو Chrome للفتح",
    pl: "Wklej w Safari lub Chrome, aby otworzyć",
    ja: "SafariまたはChromeに貼り付けて開く",
    ko: "열기 위해 Safari 또는 Chrome에 붙여넣기",
    id: "Tempel di Safari atau Chrome untuk membuka",
  },
  longPressHint: {
    en: "Long-press here to open in Safari",
    es: "Mantén pulsado aquí para abrir en Safari",
    de: "Hier lange drücken um in Safari zu öffnen",
    fr: "Maintenez ici pour ouvrir dans Safari",
    it: "Tieni premuto qui per aprire in Safari",
    pt: "Mantenha pressionado aqui para abrir no Safari",
    nl: "Houd hier ingedrukt om in Safari te openen",
    tr: "Safari'de açmak için burada uzun basın",
    ar: "اضغط مطولاً هنا للفتح في Safari",
    pl: "Naciśnij i przytrzymaj tutaj, aby otworzyć w Safari",
    ja: "ここを長押しでSafariで開く",
    ko: "Safari에서 열려면 여기를 길게 누르세요",
    id: "Tekan lama di sini untuk membuka di Safari",
  },
  loading: {
    en: "Loading...",
    es: "Cargando...",
    de: "Laden...",
    fr: "Chargement...",
    it: "Caricamento...",
    pt: "Carregando...",
    nl: "Laden...",
    tr: "Yükleniyor...",
    ar: "جارٍ التحميل...",
    pl: "Ładowanie...",
    ja: "読み込み中...",
    ko: "로딩 중...",
    id: "Memuat...",
  },
  loadingVideo: {
    en: "Loading video...",
    es: "Cargando video...",
    de: "Video wird geladen...",
    fr: "Chargement de la vidéo...",
    it: "Caricamento video...",
    pt: "Carregando vídeo...",
    nl: "Video laden...",
    tr: "Video yükleniyor...",
    ar: "جارٍ تحميل الفيديو...",
    pl: "Ładowanie wideo...",
    ja: "動画を読み込み中...",
    ko: "동영상 로딩 중...",
    id: "Memuat video...",
  },
  connectingToPlayer: {
    en: "Connecting to the player…",
    es: "Conectando con el reproductor…",
    de: "Verbindung zum Player…",
    fr: "Connexion au lecteur…",
    it: "Connessione al player…",
    pt: "Conectando ao player…",
    nl: "Verbinden met speler…",
    tr: "Oynatıcıya bağlanılıyor…",
    ar: "جارٍ الاتصال بالمشغل…",
    pl: "Łączenie z odtwarzaczem…",
    ja: "プレイヤーに接続中…",
    ko: "플레이어에 연결 중…",
    id: "Menghubungkan ke pemutar…",
  },
  inAppBrowserVideoLoadHint: {
    en: "Instagram / Facebook browser can be a bit slower — thanks for your patience.",
    es: "En el navegador de Instagram o Facebook la carga puede tardar un poco más.",
    de: "Im Instagram-/Facebook-Browser kann es etwas länger dauern.",
    fr: "Dans le navigateur Instagram/Facebook, le chargement peut être un peu plus lent.",
    it: "Nel browser di Instagram/Facebook il caricamento può richiedere più tempo.",
    pt: "No navegador do Instagram/Facebook o carregamento pode demorar um pouco mais.",
    nl: "In de Instagram-/Facebook-browser kan het iets langer duren.",
    tr: "Instagram / Facebook tarayıcısı biraz yavaş olabilir — sabrınız için teşekkürler.",
    ar: "متصفح Instagram / Facebook قد يكون أبطأ قليلاً — شكراً لصبرك.",
    pl: "Przeglądarka Instagram/Facebook może być nieco wolniejsza — dziękujemy za cierpliwość.",
    ja: "Instagram / Facebookブラウザは少し遅い場合があります — ご辛抱ありがとうございます。",
    ko: "Instagram / Facebook 브라우저는 조금 느릴 수 있습니다 — 인내심에 감사드립니다.",
    id: "Browser Instagram / Facebook mungkin sedikit lebih lambat — terima kasih atas kesabaran Anda.",
  },
  inAppBrowserMenuHint: {
    en: "Tip: tap ⋮ or \"…\" (top) → Open in browser for smoother playback.",
    es: "Tip: tocá ⋮ o \"…\" (arriba) → Abrir en el navegador para mejor reproducción.",
    de: "Tipp: ⋮ oben → Im Browser öffnen für flüssigere Wiedergabe.",
    fr: "Astuce : ⋮ en haut → Ouvrir dans le navigateur pour une lecture plus fluide.",
    it: "Suggerimento: ⋮ in alto → Apri nel browser per una riproduzione più fluida.",
    pt: "Dica: ⋮ no topo → Abrir no navegador para reprodução mais suave.",
    nl: "Tip: ⋮ bovenaan → Openen in browser voor vloeiender afspelen.",
    tr: "İpucu: ⋮ veya \"…\" (üstte) → Daha iyi oynatma için tarayıcıda aç.",
    ar: "تلميح: اضغط ⋮ أو \"…\" (أعلى) → فتح في المتصفح لتشغيل أفضل.",
    pl: "Wskazówka: dotknij ⋮ lub \"…\" (górny) → Otwórz w przeglądarce dla płynniejszego odtwarzania.",
    ja: "ヒント: ⋮ または「…」（上部）→ ブラウザで開くとスムーズに再生できます。",
    ko: "팁: ⋮ 또는 \"…\" (상단) → 원활한 재생을 위해 브라우저에서 열기.",
    id: "Tips: ketuk ⋮ atau \"…\" (atas) → Buka di browser untuk pemutaran lebih lancar.",
  },
  startingPlayback: {
    en: "Starting playback…",
    es: "Iniciando reproducción…",
    de: "Wiedergabe wird gestartet…",
    fr: "Démarrage de la lecture…",
    it: "Avvio riproduzione…",
    pt: "Iniciando reprodução…",
    nl: "Afspelen starten…",
    tr: "Oynatma başlatılıyor…",
    ar: "جارٍ بدء التشغيل…",
    pl: "Uruchamianie odtwarzania…",
    ja: "再生を開始中…",
    ko: "재생 시작 중…",
    id: "Memulai pemutaran…",
  },
  slowConnection: {
    en: "Slow connection detected. Still loading your video...",
    es: "Detectamos red lenta. Seguimos cargando el video...",
    de: "Langsame Verbindung erkannt. Video wird noch geladen...",
    fr: "Connexion lente détectée. Chargement en cours...",
    it: "Connessione lenta rilevata. Caricamento in corso...",
    pt: "Conexão lenta detectada. Ainda carregando o vídeo...",
    nl: "Trage verbinding gedetecteerd. Video wordt nog geladen...",
    tr: "Yavaş bağlantı algılandı. Video hâlâ yükleniyor...",
    ar: "تم اكتشاف اتصال بطيء. لا يزال الفيديو يُحمَّل...",
    pl: "Wykryto wolne połączenie. Wideo nadal się ładuje...",
    ja: "遅い接続を検出しました。動画をまだ読み込み中...",
    ko: "느린 연결이 감지되었습니다. 동영상을 계속 로딩 중...",
    id: "Koneksi lambat terdeteksi. Video masih dimuat...",
  },
  optimizingStream: {
    en: "Optimizing stream quality...",
    es: "Optimizando calidad para tu conexión...",
    de: "Stream-Qualität wird optimiert...",
    fr: "Optimisation de la qualité du flux...",
    it: "Ottimizzazione della qualità dello streaming...",
    pt: "Otimizando qualidade do stream...",
    nl: "Streamkwaliteit optimaliseren...",
    tr: "Bağlantınız için kalite optimize ediliyor...",
    ar: "جارٍ تحسين جودة الدفق...",
    pl: "Optymalizacja jakości strumienia...",
    ja: "ストリーム品質を最適化中...",
    ko: "스트림 품질 최적화 중...",
    id: "Mengoptimalkan kualitas streaming...",
  },
  decodingPreview: {
    en: "Caching the opening frames…",
    es: "Preparando los primeros fotogramas…",
    de: "Erste Bilder werden gepuffert…",
    fr: "Mise en cache des premières images…",
    it: "Preparazione dei primi fotogrammi…",
    pt: "Preparando os primeiros quadros…",
    nl: "Eerste beelden bufferen…",
    tr: "Açılış kareler önbelleğe alınıyor…",
    ar: "جارٍ تخزين الإطارات الأولى مؤقتاً…",
    pl: "Buforowanie pierwszych klatek…",
    ja: "最初のフレームをキャッシュ中…",
    ko: "첫 번째 프레임 캐싱 중…",
    id: "Menyimpan frame awal ke cache…",
  },
  playbackReadyBrief: {
    en: "Video ready — starting playback.",
    es: "Video listo — iniciando reproducción.",
    de: "Video bereit — Wiedergabe startet.",
    fr: "Vidéo prête — lecture imminente.",
    it: "Video pronto — avvio della riproduzione.",
    pt: "Vídeo pronto — iniciando reprodução.",
    nl: "Video klaar — afspelen start.",
    tr: "Video hazır — oynatma başlıyor.",
    ar: "الفيديو جاهز — بدء التشغيل.",
    pl: "Wideo gotowe — odtwarzanie startuje.",
    ja: "動画の準備ができました — 再生を開始します。",
    ko: "동영상 준비 완료 — 재생을 시작합니다.",
    id: "Video siap — memulai pemutaran.",
  },
};

function t(key: TranslationKey, lang: SupportedLanguage): string {
  return translations[key][lang] || translations[key].en;
}

const MuxVideoPlayerWithCTA: React.FC<MuxVideoPlayerWithCTAProps> = ({
  // Service-based configuration
  service,
  countryCode, // When passed (e.g. BioLinkTemplate overlay), used for pricing; else fallback to detected
  language, // Will be auto-detected if not provided

  // Static page – different video + CTA per route
  page: pageProp,

  // Manual configuration (overrides service/page if provided)
  playbackId: manualPlaybackId,
  forceManualPlaybackId = false,
  title: manualTitle,
  ctaUrl: manualCtaUrl,
  ctaLabel: manualCtaLabel,
  ctaSubtitle,
  premiumSimpleMembershipFreeFunnel = false,
  muxFriendlyFunnel = null,
  openInNewTab = false,
  hideUnmutePrompt = false,
  preferAutoplayWithSound = true,
  skipWorkerTemplateCta = false,
  externalCheckoutCta = false,

  // Country-specific video configurations
  playbackIdByCountry,
  ctaUrlByCountry,

  displayMode = "auto",
  loop = true,
  autoPlay = "any",
  variant = "fullscreen",
  overridePackId,
  onReady,
  onPlay,
  onPlaying: onPlayingProp,
  onEnded,
  fallbackPlaybackIds,
  parentOwnsPlaybackRotation = false,
  onCTAClick,
  onClose,
  onPlaybackTelemetry,
  onError: onErrorProp,
  muxDataVideoTitle,
  muxDataVideoSeries,
  slotKey,
  className = "",
  containerClassName = "",
  fullViewportOnMobile = false,
  analyticsFlowSurface,
  hideLoadingShimmer = false,
}) => {
  useEffect(() => {
    muxPlayerAnalyticsFlowSurface = analyticsFlowSurface;
    return () => {
      if (muxPlayerAnalyticsFlowSurface === analyticsFlowSurface) {
        muxPlayerAnalyticsFlowSurface = undefined;
      }
    };
  }, [analyticsFlowSurface]);

  const effectivePackId = overridePackId ?? "channel_invite";
  const { pathname } = useLocation();
  const { getPackPricing, packs } = usePackConfig();
  const [dashboardPlanFallback, setDashboardPlanFallback] = useState<{
    amountCents: number;
    currency: string;
    productName: string;
  } | null>(null);

  // A/B slot: session-stable variant selection when slotKey is provided.
  // Returns playbackId: null when slot is disabled, has no enabled variants, or API is unavailable.
  const {
    playbackId: slotPlaybackId,
    variantLabel: slotVariantLabel,
  } = useVideoAbSlot(slotKey ?? "");

  // Detect user's country on mount - initialize immediately for correct pricing/video
  // This runs synchronously on first render to avoid flash of wrong content
  const [detectedCountry, setDetectedCountry] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const server = getServerTemplateConfig();
    const resolved = resolveCheckoutCountryCode(server?.country);
    return resolved !== "UNKNOWN" ? resolved : detectCountryCode();
  });

  // Hidrata MUX/MP desde /api/public/brand-runtime-config (CF Pages mirror sin VITE_* en build).
  const [brandRuntimeHydrated, setBrandRuntimeHydrated] = useState(() => {
    if (manualPlaybackId?.trim()) return true;
    return Boolean(
      resolveMuxCtaPlaybackFallback(countryCode ?? detectedCountry ?? undefined),
    );
  });

  // Re-leer país tras el primer paint: Worker __TEMPLATE_CONFIG__ + cookies + timezone.
  useEffect(() => {
    void prefetchBrandRuntimeConfig().finally(() => {
      setBrandRuntimeHydrated(true);
    });
    const sync = () => {
      const server = getServerTemplateConfig();
      const resolved = resolveCheckoutCountryCode(server?.country);
      const next = resolved !== "UNKNOWN" ? resolved : detectCountryCode();
      if (next) {
        setDetectedCountry((prev) => (prev === next ? prev : next));
      }
    };
    sync();
    const raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Network fallback: when playback fails, try English playback ID (para que siempre haya video)
  const [networkErrorFallbackPlaybackId, setNetworkErrorFallbackPlaybackId] =
    useState<string | null>(null);
  const hasTriedFallbackRef = useRef(false);
  const triedFallbackPlaybackIdsRef = useRef<Set<string>>(new Set());

  // Stable ref for onError prop — avoids adding it as dep to handleError useCallback
  const onErrorRef = useRef(onErrorProp);
  useEffect(() => { onErrorRef.current = onErrorProp; }, [onErrorProp]);

  const reportFatalErrorToParent = useCallback((message: string) => {
    try {
      const msg = message.slice(0, 200) || "video_error";
      onErrorRef.current?.(msg);
    } catch {
      /* never break UX */
    }
  }, []);

  // Auto-detect language from browser if not provided
  const detectedLanguage: SupportedLanguage =
    language ||
    (() => {
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith("es")) return "es";
      if (browserLang.startsWith("de")) return "de";
      if (browserLang.startsWith("fr")) return "fr";
      if (browserLang.startsWith("it")) return "it";
      if (browserLang.startsWith("pt")) return "pt";
      if (browserLang.startsWith("nl")) return "nl";
      if (browserLang.startsWith("tr")) return "tr";
      if (browserLang.startsWith("ar")) return "ar";
      if (browserLang.startsWith("pl")) return "pl";
      if (browserLang.startsWith("ja")) return "ja";
      if (browserLang.startsWith("ko")) return "ko";
      if (browserLang.startsWith("id")) return "id";
      return "en";
    })();

  // Static-page config (sync): when `page` prop or pathname matches a known static page
  const resolvedPage: StaticPageVideoPage | null =
    pageProp ??
    (!manualPlaybackId && !manualCtaUrl && !service
      ? getStaticPageFromPathname(pathname)
      : null);
  const pageConfig = resolvedPage
    ? getStaticPageVideoConfig(resolvedPage)
    : null;

  // Load configuration from service if provided (and not using page config)
  const [serviceConfig, setServiceConfig] = useState<{
    playbackId: string;
    ctaUrl: string;
    ctaLabel: string;
    title?: string;
    playbackIdByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
    ctaUrlByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
  } | null>(null);

  // Initialize isLoadingConfig based on whether we need to load service config
  // This prevents race condition where playbackId is "" during initial render
  const needsServiceConfig = Boolean(
    service && !manualPlaybackId && !manualCtaUrl && !resolvedPage,
  );
  const [isLoadingConfig, setIsLoadingConfig] = useState(needsServiceConfig);

  useEffect(() => {
    if (needsServiceConfig) {
      setIsLoadingConfig(true);
      getVideoCTAConfigWithPayment(service!, countryCode, detectedLanguage)
        .then((config) => {
          if (config.hasVideo) {
            setServiceConfig({
              playbackId: config.playbackId,
              ctaUrl: config.ctaUrl,
              ctaLabel: getLocalizedLabel(config, language),
              title: getLocalizedTitle(config, language),
              playbackIdByCountry: config.playbackIdByCountry,
              ctaUrlByCountry: config.ctaUrlByCountry,
            });
          } else {
            setServiceConfig(null);
          }
          setIsLoadingConfig(false);
        })
        .catch((error) => {
          console.error(
            "[MuxVideoPlayerWithCTA] Error loading service config:",
            error,
          );
          setIsLoadingConfig(false);
        });
    } else {
      setServiceConfig(null);
      setIsLoadingConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsServiceConfig,
    service,
    countryCode,
    language, // Use original prop, not derived detectedLanguage
  ]);

  // Select playback ID based on country if playbackIdByCountry is provided
  const getCountrySpecificPlaybackId = (): string => {
    // Priority: manual playbackIdByCountry > service config playbackIdByCountry
    const countryConfig =
      playbackIdByCountry || serviceConfig?.playbackIdByCountry;

    if (!countryConfig) return "";

    const country = countryCode ?? detectedCountry ?? "US";
    const upperCountry = country.toUpperCase();

    // Type-safe country check
    type CountryCode = keyof typeof countryConfig;

    // Check for specific country match - supports all configured countries
    if (
      upperCountry in countryConfig &&
      countryConfig[upperCountry as CountryCode]
    ) {
      if (import.meta.env.DEV) {
        console.log(
          `[MuxVideoPlayerWithCTA] Using ${country}-specific video${
            service ? ` for ${service}` : ""
          }`,
        );
      }
      return countryConfig[upperCountry as CountryCode] as string;
    }

    // Fallback to default if provided
    if (countryConfig.default) {
      if (import.meta.env.DEV) {
        console.log(
          `[MuxVideoPlayerWithCTA] Using default video for country: ${country}${
            service ? ` (${service})` : ""
          }`,
        );
      }
      return countryConfig.default;
    }

    return "";
  };

  // Select CTA URL based on country if ctaUrlByCountry is provided
  const getCountrySpecificCtaUrl = (): string => {
    // Priority: manual ctaUrlByCountry > service config ctaUrlByCountry
    const countryConfig = ctaUrlByCountry || serviceConfig?.ctaUrlByCountry;

    if (!countryConfig) {
      return "";
    }

    const country = countryCode ?? detectedCountry ?? "US";
    const upperCountry = country.toUpperCase();

    if (import.meta.env.DEV) {
      console.log(`[MuxVideoPlayerWithCTA] 🌍 Country Detection:`, {
        detectedCountry,
        countryCodeProp: countryCode,
        resolvedCountry: upperCountry,
        service,
        availableCountries: Object.keys(countryConfig).filter(
          (k) => countryConfig[k as keyof typeof countryConfig],
        ),
      });
    }

    // Type-safe country check
    type CountryCode = keyof typeof countryConfig;

    // Check for specific country match - supports all configured countries
    // This allows per-country pricing: US $14.99, ES €12.99, MX $199 MXN, etc.
    const countryUrl =
      upperCountry && upperCountry in countryConfig
        ? countryConfig[upperCountry as CountryCode]
        : null;

    if (countryUrl && countryUrl.length > 0) {
      if (import.meta.env.DEV) {
        console.log(
          `[MuxVideoPlayerWithCTA] ✅ Using ${upperCountry}-specific CTA URL${
            service ? ` for ${service}` : ""
          }:`,
          countryUrl.substring(0, 50) + "...",
        );
      }
      // Exterior policy: bloquear fan.* en configs por país también.
      if (upperCountry !== "AR" && /\/\/[^/]*fan\./i.test(countryUrl as string)) {
        console.warn(`[MuxVideoPlayerWithCTA] ctaUrlByCountry[${upperCountry}] es fan.*; anulando`);
        return "";
      }
      return countryUrl as string;
    }

    // Fallback to default if provided
    if (countryConfig.default) {
      if (import.meta.env.DEV) {
        console.log(
          `[MuxVideoPlayerWithCTA] ⚠️ No specific URL for ${upperCountry}, using default${
            service ? ` (${service})` : ""
          }:`,
          countryConfig.default.substring(0, 50) + "...",
        );
      }
      if (upperCountry !== "AR" && /\/\/[^/]*fan\./i.test(countryConfig.default)) {
        console.warn(`[MuxVideoPlayerWithCTA] default ctaUrlByCountry es fan.* para ${upperCountry}; anulando`);
        return "";
      }
      return countryConfig.default;
    }

    return "";
  };

  // =============================================================================
  // PACK CONFIG API INTEGRATION (Feature Flag)
  // =============================================================================

  // Feature flag: enable/disable API-driven configuration
  const USE_API_CONFIG = import.meta.env.VITE_USE_PACK_CONFIG_API === "true";

  // Single source of truth for country: prefer explicit prop (e.g. from BioLinkTemplate/Worker) so Chile gets CL and correct $19.99
  const effectiveCountryForApi = countryCode ?? detectedCountry ?? "US";

  // Get pack from API if feature flag is enabled and service is provided
  // IMPORTANT: Always call hooks (React rules), use result conditionally
  const pack: PackConfig | null =
    USE_API_CONFIG && service ? (packs[service] as PackConfig) || null : null;

  // Resolve configuration from API with fallbacks
  // This always runs (React rules), but uses pack=null when flag is off → uses only fallbacks
  const apiVideoConfig = usePackConfigForVideo(
    pack,
    effectiveCountryForApi,
    getUserLanguage(),
    {
      // Fallback 1: Static configuration (telegramGroupsVideo)
      playbackIdFallback: (cc) => getTelegramPlaybackIdByCountry(cc),
      playbackIdHardcoded:
        getMuxCtaUniversalPlaybackId() ||
        (import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string),
      ctaUrlFallback: (cc) => getTelegramCTAUrlByCountry(cc),
      ctaLabelFallback: (lang) => (lang === "es" ? "Unirte" : "Join"),
    },
  );

  // =============================================================================
  // RESOLUTION PRIORITY (with API integration)
  // =============================================================================

  // Priority: slot (A/B) > manual props > API config > country-specific > runtime fallbacks.
  // forceManualPlaybackId con manual vacío (CF Pages sin VITE_*) → igualmente usa fallbacks runtime.
  const manualTrimmed = manualPlaybackId?.trim() || "";
  const runtimePlaybackFallback = resolveMuxCtaPlaybackFallback(
    countryCode ?? detectedCountry,
  );
  const resolveConfiguredPlaybackId = (): string => {
    if (service && apiVideoConfig.playbackId) return apiVideoConfig.playbackId;
    const byCountry = getCountrySpecificPlaybackId();
    if (byCountry) return byCountry;
    if (pageConfig?.playbackId) return pageConfig.playbackId;
    if (serviceConfig?.playbackId) return serviceConfig.playbackId;
    const telegramByCountry = getTelegramPlaybackIdByCountry(
      countryCode ?? detectedCountry ?? "US",
    );
    if (telegramByCountry) return telegramByCountry;
    const universal = getMuxCtaUniversalPlaybackId();
    if (universal) return universal;
    if (TELEGRAM_GROUPS_EN_PLAYBACK_ID) return TELEGRAM_GROUPS_EN_PLAYBACK_ID;
    if (HARDCODED_FALLBACK_PLAYBACK_ID) return HARDCODED_FALLBACK_PLAYBACK_ID;
    if (runtimePlaybackFallback) return runtimePlaybackFallback;
    return coalesceMuxPlaybackId(null);
  };
  const playbackId =
    slotPlaybackId || (manualTrimmed ? manualTrimmed : resolveConfiguredPlaybackId());
  let ctaUrl =
    manualCtaUrl ||
    (service ? apiVideoConfig.ctaUrl : null) ||
    getCountrySpecificCtaUrl() ||
    pageConfig?.ctaUrl ||
    serviceConfig?.ctaUrl ||
    "";
  let ctaLabel =
    manualCtaLabel ||
    (service ? apiVideoConfig.ctaLabel : null) ||
    pageConfig?.ctaLabel ||
    serviceConfig?.ctaLabel ||
    "Unirte";

  if (!skipWorkerTemplateCta) {
    const workerCta = resolveWorkerDrivenCta();
    if (workerCta.mode === "hidden") {
      ctaUrl = "";
    } else if (workerCta.mode === "worker") {
      ctaUrl = workerCta.url;
      ctaLabel = getWorkerCtaDisplayLabel(detectedLanguage, workerCta.variant);
    }
  }

  // Política exterior (refuerzo final): cualquier ctaUrl que termine siendo fan.* para !AR se anula.
  // Prioriza el flujo interno de Stripe dentro del video (para channel_invite, custom packs, etc.).
  const isExteriorHere = (countryCode ?? detectedCountry ?? "").toUpperCase() !== "AR";
  if (isExteriorHere && ctaUrl && /\/\/[^/]*fan\./i.test(ctaUrl)) {
    console.warn(
      "[MuxVideoPlayerWithCTA] ctaUrl apunta a fan.* para exterior (post-resolución); anulando",
      ctaUrl,
    );
    ctaUrl = "";
  }
  const title = manualTitle || pageConfig?.title || serviceConfig?.title;

  const muxFriendlyBanner = useMemo(
    () =>
      getMuxCtaFriendlyBannerMessage(
        detectedLanguage,
        {
          service,
          ctaSubtitle,
          staticPage: resolvedPage,
          premiumSimpleMembershipFreeFunnel,
        },
        muxFriendlyFunnel,
      ),
    [
      detectedLanguage,
      service,
      ctaSubtitle,
      resolvedPage,
      premiumSimpleMembershipFreeFunnel,
      muxFriendlyFunnel,
    ],
  );

  // Log API config usage (development only)
  useEffect(() => {
    if (import.meta.env.DEV && USE_API_CONFIG && service) {
      console.log("[MuxVideoPlayerWithCTA] API Config:", {
        enabled: USE_API_CONFIG,
        service,
        pack: pack?.id || "null",
        country: effectiveCountryForApi,
        isActive: apiVideoConfig.isActive,
        playbackId: apiVideoConfig.playbackId?.substring(0, 20) + "...",
        ctaUrl: apiVideoConfig.ctaUrl,
        ctaLabel: apiVideoConfig.ctaLabel,
        pricing: `${apiVideoConfig.priceFormatted} (${apiVideoConfig.currency})`,
      });
    }
  }, [USE_API_CONFIG, service, pack, effectiveCountryForApi, apiVideoConfig]);

  // Single source of truth for country in custom checkout (pricing + API body fallback + tracking)
  // Note: effectiveCountryForApi is defined above for API resolution
  const effectiveCountry = effectiveCountryForApi;

  const stripeWatcherLuckEligible = useMemo(() => {
    if (getWatcherLuckDiscountMaxPercentClient() <= 0) return false;
    if (!STRIPE_PUBLISHABLE_KEY) return false;
    if (!(service === "channel_invite" || Boolean(overridePackId)))
      return false;
    if (effectiveCountry === "AR" && effectivePackId === "channel_invite")
      return false;
    return true;
  }, [service, overridePackId, effectiveCountry, effectivePackId]);

  const stripeWatcherLuckPercent = useMemo(() => {
    if (!stripeWatcherLuckEligible) return 0;
    return getOrCreateSessionWatcherLuckPercent();
  }, [stripeWatcherLuckEligible]);

  const fallbackPlanTier = useMemo(() => {
    // Fallback canónico desde /dashboard/subscripciones/planes:
    // - AR   → basic (MercadoPago)
    // - intl → vip   (Stripe)
    // Contexto de prod: el Worker inyecta país real en __TEMPLATE_CONFIG__.
    // Si no hay AR explícito, el fallback debe ser Stripe (vip).
    const resolvedCountry = String(
      countryCode ?? getServerTemplateConfig()?.country ?? detectedCountry ?? "",
    )
      .trim()
      .toUpperCase();
    if (resolvedCountry === "AR") return "basic";
    // Default no-AR (incluye UNKNOWN/missing): Stripe VIP.
    return "vip";
  }, [countryCode, detectedCountry]);

  useEffect(() => {
    if (!fallbackPlanTier) {
      setDashboardPlanFallback(null);
      return;
    }
    let cancelled = false;
    // fetchPlanPricing dedupea con el fetch gemelo de DualCTAVideoLanding —
    // antes eran dos requests idénticos en el arranque (replay 019f6c0a…).
    fetchPlanPricing(fallbackPlanTier)
      .then((data) => {
        if (cancelled || !data) return;
        const amount = Number(data.amount_cents ?? 0);
        const currency = String(data.currency ?? "usd").trim().toLowerCase();
        if (!Number.isFinite(amount) || amount <= 0 || !currency) return;
        setDashboardPlanFallback({
          amountCents: Math.round(amount),
          currency,
          productName: String(data.product_name ?? "").trim() || "Membership",
        });
      })
      .catch(() => {
        /* best-effort fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [fallbackPlanTier]);

  // Check if content is active: only false when API explicitly returns isActive: false for this country.
  // When API has no entry for the country or fails, fallback config is used and content stays active.
  const isContentActive =
    USE_API_CONFIG && service ? apiVideoConfig.isActive : true;

  // Use fallback English playback ID when connection/CDN error occurred (PE, ES, etc.)
  const effectivePlaybackId = networkErrorFallbackPlaybackId || playbackId;

  // Validate playbackId - must be non-empty and valid format
  const isValidPlaybackId =
    effectivePlaybackId &&
    effectivePlaybackId.trim().length > 0 &&
    effectivePlaybackId !== SAFE_PLAYBACK_ID_FALLBACK &&
    effectivePlaybackId.length >= 10; // Mux playback IDs are typically 20+ chars

  // Log playbackId validation on mount (only errors in production)
  useEffect(() => {
    // Only log in development or if there's an error
    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Component initialized:", {
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId?.substring(0, 20) + "...",
        isValid: isValidPlaybackId,
        source: manualPlaybackId
          ? "manual"
          : serviceConfig
            ? "service"
            : pageConfig
              ? "page"
              : "none",
      });
    }

    if (!isValidPlaybackId) {
      const errorMsg = !effectivePlaybackId
        ? "PlaybackId is empty - check env vars in Vercel"
        : `Invalid playbackId: "${effectivePlaybackId}" (length: ${effectivePlaybackId.length})`;

      console.error(`[MuxVideoPlayerWithCTA] ❌ ${errorMsg}`);

      logVideoEvent({
        event: "playback_id_invalid",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId || "EMPTY",
        errorMessage: errorMsg,
      });
    }
  }, [
    isValidPlaybackId,
    effectivePlaybackId,
    effectiveCountry,
    service,
    manualPlaybackId,
    serviceConfig,
    pageConfig,
  ]);
  const getFallbackPlaybackIdForErrors = useCallback((): string | null => {
    if (manualPlaybackId && manualPlaybackId.trim() && !service) {
      const alts = fallbackPlaybackIds ?? [];
      const tried = triedFallbackPlaybackIdsRef.current;
      tried.add(effectivePlaybackId);
      for (const candidate of alts) {
        const trimmed = candidate?.trim();
        if (!trimmed || tried.has(trimmed)) continue;
        return trimmed;
      }
      return null;
    }
    const countryConfig =
      playbackIdByCountry || serviceConfig?.playbackIdByCountry;

    // Priority: default country video -> US video -> base service video -> EN fallback -> hardcoded fallback
    // This keeps video available when a country-specific playback ID is invalid in Mux.
    // HARDCODED_FALLBACK_PLAYBACK_ID (local) is guaranteed valid (not from env vars).
    const candidates = [
      countryConfig?.default,
      countryConfig?.US,
      serviceConfig?.playbackId,
      getMuxCtaUniversalPlaybackId(),
      TELEGRAM_GROUPS_EN_PLAYBACK_ID,
      HARDCODED_FALLBACK_PLAYBACK_ID, // Last resort - guaranteed valid
    ];

    for (const candidate of candidates) {
      if (!candidate || candidate.trim() === "") continue;
      if (candidate === effectivePlaybackId) continue;
      return candidate;
    }

    return null;
  }, [
    manualPlaybackId,
    service,
    fallbackPlaybackIds,
    playbackIdByCountry,
    serviceConfig?.playbackIdByCountry,
    serviceConfig?.playbackId,
    effectivePlaybackId,
  ]);

  // Reset fallback only when country explicitly changes (e.g. test_country) - not on initial detection
  const prevCountryRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevCountryRef.current;
    prevCountryRef.current = effectiveCountry;
    if (prev !== undefined && prev !== effectiveCountry) {
      setNetworkErrorFallbackPlaybackId(null);
      hasTriedFallbackRef.current = false;
    }
  }, [effectiveCountry]);

  const hasImmediatePlayback =
    Boolean(manualTrimmed) ||
    Boolean(slotPlaybackId) ||
    Boolean(pageConfig?.playbackId);
  const playbackConfigPending =
    isLoadingConfig ||
    (!brandRuntimeHydrated && !hasImmediatePlayback && Boolean(needsServiceConfig || service));
  const missingPlaybackLoggedRef = useRef(false);
  useEffect(() => {
    if (playbackConfigPending) return;
    if (effectivePlaybackId?.trim()) return;
    if (missingPlaybackLoggedRef.current) return;
    missingPlaybackLoggedRef.current = true;
    logVideoErrorProduction({
      event: "video_error",
      country: effectiveCountry,
      service: service ?? undefined,
      errorType: "config_missing",
      errorMessage: "No playbackId provided",
    });
    console.error("[MuxVideoPlayerWithCTA] ❌ No playbackId provided", {
      manualPlaybackId,
      service,
      page: pageProp,
      pathname: location.pathname,
      countryCode,
    });
  }, [
    playbackConfigPending,
    effectivePlaybackId,
    effectiveCountry,
    service,
    manualPlaybackId,
    pageProp,
    pathname,
    countryCode,
  ]);

  const showInstagramIcon = pageConfig?.showInstagramIcon ?? false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false); // Start as false, will be set to true when playing
  // Mobile browsers (Chrome Android, Safari iOS, IG/FB IABs) block unmuted
  // autoplay (NotAllowedError). Start muted so MuxPlayer's `muted` prop and our
  // autoplay effect share one path — avoids NotAllowedError → muted play()
  // AbortError races that leave DualCTA stuck with video_stuck_not_playing.
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (isInstagramBrowser()) return true;
    } catch {
      /* ignore */
    }
    const ua = navigator.userAgent || "";
    const ios =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const android = /Android/i.test(ua);
    const coarsePointer =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.innerWidth < 900;
    return (
      ios ||
      android ||
      coarsePointer ||
      (navigator.maxTouchPoints > 0 && narrow)
    );
  });
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loading state for video
  const [showPlayButton, setShowPlayButton] = useState(false); // Show play button if autoplay fails
  const [showMuteIndicator, setShowMuteIndicator] = useState(false); // Show mute indicator on mobile
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const MAX_RETRIES = 3; // Maximum number of retry attempts
  const [thumbnailError, setThumbnailError] = useState(false); // Track if thumbnail failed to load
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false); // Hide visual loading only after real playback starts
  const hasPlaybackStartedRef = useRef(false);
  const [hasReceivedMuxMetadata, setHasReceivedMuxMetadata] = useState(false);
  /** First decoded frame (`loadeddata`) — better perceived progress than manifest alone (Mux/HTML5). */
  const [muxHasLoadedData, setMuxHasLoadedData] = useState(false);
  /** Enough buffer to start playback (`canplay` / preload segments). */
  const [muxCanPlayBuffered, setMuxCanPlayBuffered] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0); // Real elapsed loading time
  const [networkTypeHint, setNetworkTypeHint] = useState("unknown");
  const [saveDataEnabled, setSaveDataEnabled] = useState(false);

  // Checkout state management
  const [checkoutState, setCheckoutState] =
    useState<CheckoutState>("VIDEO_PREVIEW");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);
  /** Full-screen "please wait" before hard navigations (router / checkout page / external URL). */
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const checkoutStateRef = useRef(checkoutState);
  checkoutStateRef.current = checkoutState;
  const muxStripeJsUnavailableTrackedRef = useRef(false);

  // Device detection hooks for platform-specific optimizations
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  const isAndroid = useIsAndroid();
  const muxDesktopMacMseHardening = useMuxDesktopMacMseHardening();
  const isLandscape = useIsLandscape();
  const isInstagram = isInstagramBrowser();
  const mpIgLang = (detectedLanguage?.split("-")[0] as "es" | "en" | "pt") ?? "es";
  const { interceptMercadoPagoCta, overlay: mpIgEscapeOverlay } =
    useMercadoPagoInstagramEscape({
      language: mpIgLang,
      surface: "mux_video_cta",
      countryCode: effectiveCountry,
    });
  const muxPlayerMuted = preferAutoplayWithSound ? isMuted : isInstagram || isMuted;
  /** MSE endurecido: Chromium en macOS (incl. Arc sin token en UA) + Arc explícito; no Instagram. */
  const muxArcPlaybackTune = muxDesktopMacMseHardening && !isInstagram;
  /** Android IG IAB: native demuxer often hits DEMUXER_ERROR_COULD_NOT_PARSE on High-profile HLS. */
  const instagramAndroidMseTune = isInstagram && isAndroid;
  /** Menos presión sobre SourceBuffer de audio/vídeo (fallos appendBuffer en Chromium macOS / IG Android). */
  const fragileMseHlsConfig: Partial<HlsConfig> | undefined = useMemo(
    () => {
      if (muxArcPlaybackTune || instagramAndroidMseTune) {
        return {
          lowLatencyMode: false,
          maxBufferLength: instagramAndroidMseTune ? 18 : 25,
          maxMaxBufferLength: instagramAndroidMseTune ? 36 : 50,
          backBufferLength: instagramAndroidMseTune ? 30 : 60,
          maxBufferHole: 0.5,
          nudgeOffset: 0.05,
          nudgeMaxRetry: 5,
          maxFragLookUpTolerance: 0.25,
          fragLoadingTimeOut: 20000,
        };
      }
      return undefined;
    },
    [muxArcPlaybackTune, instagramAndroidMseTune],
  );
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const parentCtaLockRef = useRef(false);
  const parentCtaUnlockTimerRef = useRef<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Instagram browser banner state (dismissible)
  const [showInstagramBanner, setShowInstagramBanner] = useState(false);
  const [instagramBannerDismissed, setInstagramBannerDismissed] =
    useState(false);
  const [linkCopiedForBrowser, setLinkCopiedForBrowser] = useState(false);

  /** Stripe-only fleeting toast: "lucky" bonus discount rolled for this tab session */
  const [showWatcherLuckBanner, setShowWatcherLuckBanner] = useState(false);
  const watcherLuckToastShownRef = useRef(false);
  const watcherLuckToastTimerRef = useRef<number | null>(null);
  const watcherLuckWallTimerRef = useRef<number | null>(null);
  const watcherLuckTUThrottleRef = useRef(0);

  // Instagram "open in browser" banner — delayed so it never covers the critical
  // first-seconds unmute prompt (session 019f43b4: banner @1s hid "Toca para escuchar",
  // user watched full teaser muted and bounced without CTA).
  // Desktop browsers already ARE the browser, so never surface "open in browser" there.
  useEffect(() => {
    if (!isInstagram || !isMobile || instagramBannerDismissed) {
      setShowInstagramBanner(false);
      return;
    }
    // Wait until playback started; if still muted, wait longer so unmute stays primary.
    if (!isPlaying) return;
    const delayMs = isMuted ? 14000 : 6000;
    const timer = setTimeout(() => {
      setShowInstagramBanner(true);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [isInstagram, isMobile, instagramBannerDismissed, isPlaying, isMuted]);

  // Apply Instagram browser CSS optimizations
  useEffect(() => {
    if (isInstagram) {
      applyInstagramBrowserCSS();
    }
  }, [isInstagram]);
  useEffect(() => {
    return () => {
      if (parentCtaUnlockTimerRef.current !== null) {
        clearTimeout(parentCtaUnlockTimerRef.current);
      }
      if (watcherLuckToastTimerRef.current !== null) {
        clearTimeout(watcherLuckToastTimerRef.current);
        watcherLuckToastTimerRef.current = null;
      }
      if (watcherLuckWallTimerRef.current !== null) {
        clearTimeout(watcherLuckWallTimerRef.current);
        watcherLuckWallTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (checkoutState === "VIDEO_PREVIEW") return;
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
  }, [checkoutState]);
  const safeCssId = sanitizePlaybackIdForCss(effectivePlaybackId ?? "");
  // FIX flash: lock del MuxPlayer key al PRIMER playbackId no-vacío. Los cambios
  // posteriores de effectivePlaybackId se manejan via actualización del atributo
  // playback-id del <mux-player> (sin remount). Solo retryCount (error recovery)
  // fuerza remount. Antes el key incluía safeCssId → cada cambio de ID remontaba
  // el player entero → flash/reload visible.
  const initialPlayerKeyRef = useRef("");
  if (!initialPlayerKeyRef.current && effectivePlaybackId) {
    initialPlayerKeyRef.current = safeCssId;
  }
  const muxPlayerKey = `muxp-${initialPlayerKeyRef.current || safeCssId}-r${retryCount}`;
  const ctaUrlValid = Boolean(ctaUrl && isAllowedRedirectUrl(ctaUrl));
  // Mostrar botón si hay URL válida O custom checkout (overridePackId + Stripe) para /sorpresa, /exclusive
  const showCTAButton =
    ctaUrlValid || (Boolean(overridePackId) && Boolean(STRIPE_PUBLISHABLE_KEY));
  const ctaBusy = isCreatingPaymentIntent || isNavigatingAway;

  const muxPaywallPriceShown = useMemo(() => {
    if (!overridePackId) return ctaLabel || undefined;
    const pricing =
      getPackPricing(overridePackId, effectiveCountry) ??
      getPackPricingStatic(overridePackId, effectiveCountry);
    if (!pricing) return ctaLabel || undefined;
    return (
      (pricing as { displayAmount?: string }).displayAmount ||
      ctaLabel ||
      undefined
    );
  }, [overridePackId, effectiveCountry, getPackPricing, ctaLabel]);

  const muxCtaImpressionTrackedRef = useRef(false);
  const muxCtaBlockedTrackedRef = useRef(false);
  const paywallTrackingReady =
    !isLoadingConfig &&
    (brandRuntimeHydrated || hasImmediatePlayback);

  const buildMuxPaywallCtx = useCallback(
    () => ({
      surface: "mux_video_player" as const,
      playbackId: effectivePlaybackId,
      videoTitle: manualTitle || muxDataVideoTitle,
      priceShown: muxPaywallPriceShown,
      packName: overridePackId,
      // Coherence: explicit source for all Mux → conversion events
      source: BRAND_SOURCE_KEY,
      extra: {
        service: service ?? null,
        analytics_flow_surface: analyticsFlowSurface ?? null,
        slot_key: slotKey ?? null,
      },
    }),
    [
      effectivePlaybackId,
      manualTitle,
      muxDataVideoTitle,
      muxPaywallPriceShown,
      overridePackId,
      service,
      analyticsFlowSurface,
      slotKey,
    ],
  );

  useEffect(() => {
    if (!paywallTrackingReady) return;
    if (showCTAButton) {
      if (muxCtaImpressionTrackedRef.current) return;
      muxCtaImpressionTrackedRef.current = true;
      trackMuxCtaViewed({
        ...buildMuxPaywallCtx(),
        trigger: "mux_cta_impression",
      });
      return;
    }
    if (onCTAClick || externalCheckoutCta || muxCtaBlockedTrackedRef.current) return;
    muxCtaBlockedTrackedRef.current = true;
    trackMuxCtaBlocked({
      ...buildMuxPaywallCtx(),
      blockReason:
        !ctaUrlValid && !overridePackId
          ? "no_valid_cta_url_or_pack"
          : overridePackId && !STRIPE_PUBLISHABLE_KEY
            ? "stripe_not_configured"
            : "cta_hidden",
    });
  }, [
    paywallTrackingReady,
    showCTAButton,
    onCTAClick,
    externalCheckoutCta,
    buildMuxPaywallCtx,
    ctaUrlValid,
    overridePackId,
  ]);

  // Debug: Log CTA configuration (solo en desarrollo)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[MuxVideoPlayerWithCTA] CTA Debug:", {
      service,
      ctaUrl,
      ctaUrlValid,
      ctaLabel,
      playbackId: playbackId?.substring(0, 20) + "...",
      serviceConfig: serviceConfig ? "loaded" : "not loaded",
      stripeKeyConfigured: Boolean(STRIPE_PUBLISHABLE_KEY),
    });
  }, [service, ctaUrl, ctaUrlValid, ctaLabel, playbackId, serviceConfig]);

  const isModal = variant === "modal";
  const effectiveDisplayMode = isModal ? "contain" : displayMode;
  const isLikelySlowNetwork =
    saveDataEnabled ||
    networkTypeHint === "slow-2g" ||
    networkTypeHint === "2g" ||
    networkTypeHint === "3g";
  const shouldShowSlowConnectionHint = loadingSeconds >= 4;

  const awaitingFirstPlayback =
    !hasPlaybackStarted &&
    !showPlayButton &&
    !isPlaying &&
    !hasError;
  const isMobileOrInstagram = isMobile || isInstagram;
  /**
   * Full-screen loader:
   * - Mobile/Instagram: stays until the video actually starts playing (`awaitingFirstPlayback` becomes false).
   *   Prevents the confusing "can play but not playing yet" gap that feels like a freeze on mobile.
   * - Desktop: hides once Mux has metadata + a playable buffer, then hands off to the light pill.
   */
  const showHeavyVideoLoader =
    !hideLoadingShimmer &&
    awaitingFirstPlayback &&
    isLoading &&
    (isMobileOrInstagram || !hasReceivedMuxMetadata || !muxCanPlayBuffered);
  /** Desktop-only minimal pill once Mux reports `canplay` but `playing` hasn't fired yet. */
  const showLightVideoLoader =
    !hideLoadingShimmer &&
    !isMobileOrInstagram &&
    awaitingFirstPlayback &&
    isLoading &&
    hasReceivedMuxMetadata &&
    muxCanPlayBuffered;
  /** Padre provee poster/spinner — ocultar preview interno del player hasta `playing`. */
  const parentOwnsPreview = hideLoadingShimmer;
  const deferMuxPlayerVisible =
    parentOwnsPreview && awaitingFirstPlayback && !hasPlaybackStarted;

  const getDisplayMode = (): "reel" | "contain" | "cover" => {
    if (effectiveDisplayMode === "auto") return "reel";
    return effectiveDisplayMode as "reel" | "contain" | "cover";
  };

  const currentDisplayMode = getDisplayMode();
  const isReelMode = currentDisplayMode === "reel";

  // While fullscreen/modal Mux CTA owns the bottom safe-area, hide the cookie dock.
  const suppressConsentDock =
    isModal || (isReelMode && isMobile) || (fullViewportOnMobile && isMobile);

  useEffect(() => {
    if (!suppressConsentDock) return;
    document.documentElement.dataset.muxCtaActive = "1";
    window.dispatchEvent(
      new CustomEvent(MUX_CTA_ACTIVE_EVENT, { detail: { active: true } }),
    );
    return () => {
      delete document.documentElement.dataset.muxCtaActive;
      window.dispatchEvent(
        new CustomEvent(MUX_CTA_ACTIVE_EVENT, { detail: { active: false } }),
      );
    };
  }, [suppressConsentDock]);

  const [modalMounted, setModalMounted] = useState(!isModal);

  useEffect(() => {
    if (!isModal) return;
    const id = requestAnimationFrame(() => setModalMounted(true));
    return () => cancelAnimationFrame(id);
  }, [isModal]);

  useEffect(() => {
    if (!onClose) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    if (!onClose) return;
    const id = requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    hasPlaybackStartedRef.current = hasPlaybackStarted;
  }, [hasPlaybackStarted]);

  useEffect(() => {
    if (manualPlaybackId?.trim()) {
      triedFallbackPlaybackIdsRef.current = new Set();
      hasTriedFallbackRef.current = false;
    }
  }, [manualPlaybackId]);

  useEffect(() => {
    if (effectivePlaybackId) {
      setHasError(false);
      setErrorMessage("");
      setIsLoading(true); // Reset loading when playback ID changes (including Spain fallback)
      setHasReceivedMuxMetadata(false);
      setMuxHasLoadedData(false);
      setMuxCanPlayBuffered(false);
      setHasPlaybackStarted(false);
      hasPlaybackStartedRef.current = false;
      setShowPlayButton(false);
      setLoadingSeconds(0);
      setRetryCount(0);
      setThumbnailError(false);

      // NOTE: We intentionally do NOT preload the .m3u8 manifest here.
      // A <link rel="preload" as="fetch" crossOrigin="anonymous"> for the HLS manifest
      // is NEVER reused by Mux's hls.js player (different XHR CORS mode), so it:
      //  1. Triggers Chrome's "preloaded but not used" warning every time
      //  2. Can cause a CORS cache-poisoning that makes the player emit a mediaError
      // The Mux player handles its own manifest fetch efficiently; no preload needed.

      // Keep loading state long enough to avoid exposing a static/non-playing frame too early.
      // It will be hidden earlier by real playback events (canplay/play).
      //
      // Why the timer is visibility-aware
      // ---------------------------------
      // Inside iOS Safari (and Instagram in-app webview in particular) media
      // buffering is suspended while the tab is hidden. Plain wall-clock
      // timers spent that suspended time as "waiting" budget, so a user who
      // bounced between IG and the page during the first 6 s — typical for
      // a notification toast or scroll-into-banner gesture — never got the
      // chance to actually buffer enough video to start playing. Real
      // example logged 2026-05-31 09:53:05 → 09:53:10 (iPhone 14, iOS 26.5,
      // IG webview, 3 hide/visible transitions, fell back to thumbnail).
      //
      // The watchdog now only counts time the document was visible. We also
      // emit the wall-clock + visibility flag in the telemetry so we can
      // see the distribution and tune `maxLoadingTime` if needed.
      const maxLoadingTime = isMobile || isInstagram ? 6000 : 8000;
      const mountedAt = Date.now();
      let foregroundMs = 0;
      let lastForegroundStartAt: number | null =
        typeof document === "undefined" || !document.hidden ? Date.now() : null;
      let wasHidden = lastForegroundStartAt == null;
      let watchdogHandle: ReturnType<typeof setTimeout> | null = null;

      const fireWatchdog = () => {
        if (hasPlaybackStartedRef.current) return;
        // Account for the foreground tail since the last visibility flip.
        if (lastForegroundStartAt != null) {
          foregroundMs += Date.now() - lastForegroundStartAt;
          lastForegroundStartAt = null;
        }
        setIsLoading(false);
        setShowPlayButton(true);
        logVideoEvent({
          event: "video_shown_thumbnail_only",
          country: effectiveCountry,
          service,
          playbackId: effectivePlaybackId,
          foregroundTimeMs: Math.round(foregroundMs),
          wallTimeMs: Date.now() - mountedAt,
          wasHidden,
        });
      };

      const scheduleWatchdog = () => {
        if (watchdogHandle != null) {
          clearTimeout(watchdogHandle);
          watchdogHandle = null;
        }
        if (lastForegroundStartAt == null) return; // tab hidden — wait for visibility
        const remaining = Math.max(0, maxLoadingTime - foregroundMs);
        watchdogHandle = setTimeout(fireWatchdog, remaining);
      };

      const onVisibilityChange = () => {
        if (hasPlaybackStartedRef.current) return;
        if (typeof document === "undefined") return;
        if (document.hidden) {
          wasHidden = true;
          if (lastForegroundStartAt != null) {
            foregroundMs += Date.now() - lastForegroundStartAt;
            lastForegroundStartAt = null;
          }
          if (watchdogHandle != null) {
            clearTimeout(watchdogHandle);
            watchdogHandle = null;
          }
        } else {
          lastForegroundStartAt = Date.now();
          scheduleWatchdog();
        }
      };

      scheduleWatchdog();
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }

      return () => {
        if (watchdogHandle != null) clearTimeout(watchdogHandle);
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      };
    }
  }, [effectivePlaybackId, isMobile, isInstagram, effectiveCountry, service]);

  // Network quality hint for user-facing loading messages (best effort)
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    };
    const connection = nav.connection;
    if (!connection) return;
    if (connection.effectiveType) setNetworkTypeHint(connection.effectiveType);
    setSaveDataEnabled(Boolean(connection.saveData));
  }, []);

  // Track real loading duration so user sees progress, not a static wait (desde entrada hasta listo / play)
  useEffect(() => {
    const inInitialMuxLoad = awaitingFirstPlayback && isLoading;
    if (!inInitialMuxLoad || hasPlaybackStarted) {
      setLoadingSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setLoadingSeconds(0);
    const timer = window.setInterval(() => {
      setLoadingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [
    awaitingFirstPlayback,
    isLoading,
    hasPlaybackStarted,
    effectivePlaybackId,
  ]);

  const handleError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      try {
        let msg = "";
        let type = "";
        let details = "";
        let url: string | undefined;
        let fatal = false;
        if (error && typeof error === "object" && "message" in error) {
          msg = String((error as { message?: string }).message ?? "");
          type = String((error as { type?: string }).type ?? "");
          details = String((error as { details?: string }).details ?? "");
          url = (error as { url?: string }).url;
          fatal = Boolean((error as { fatal?: boolean }).fatal);
        }

        const lowerMsg = msg.toLowerCase();
        const lowerDetails = details.toLowerCase();
        if (
          lowerMsg.includes("aborted") ||
          lowerDetails.includes("code 1") ||
          lowerDetails.includes("media_err_aborted")
        ) {
          return;
        }
        const isBufferAppendMux =
          lowerMsg.includes("bufferappend") ||
          lowerDetails.includes("bufferappend") ||
          lowerMsg.includes("sourcebuffer") ||
          lowerMsg.includes("appendbuffer") ||
          lowerMsg.includes("removed from the parent media source") ||
          lowerMsg.includes("invalidstateerror");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (Code 4) / "no supported sources" — load() no ayuda.
        const isNoSourceMux =
          lowerDetails.includes("code 4") ||
          lowerMsg.includes("no supported sources");

        // Ignore errors that are not related to Mux video
        // Check if error is from a blocked resource (like ad blockers blocking trackers)
        if (typeof url === "string") {
          // Ignore errors from non-Mux domains (like metricool, analytics, etc.)
          if (!url.includes("mux.com") && !url.includes("stream.mux.com")) {
            console.debug(
              "[MuxVideoPlayerWithCTA] Ignoring non-Mux error:",
              url,
            );
            return;
          }
        }

        // Only handle errors that are clearly video-related
        const isVideoError =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          msg.toLowerCase().includes("video") ||
          msg.toLowerCase().includes("playback") ||
          msg.toLowerCase().includes("stream") ||
          msg.toLowerCase().includes("mux") ||
          msg.toLowerCase().includes("manifest") ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("token") ||
          type === "mediaError" ||
          type === "networkError" ||
          details === "manifestLoadError" ||
          (typeof url === "string" &&
            (url.includes("stream.mux.com") || url.includes("mux.com")));

        // channel_invite = telegram groups, principal. NUNCA bloquear CTA.
        const isJoinFlowService = [
          "telegram",
          "telegram_groups",
          "channel_invite",
        ].includes(service ?? "");

        // If it's not a video error, ignore it
        if (!isVideoError) {
          console.debug("[MuxVideoPlayerWithCTA] Ignoring non-video error:", {
            msg,
            type,
            details,
            url,
          });
          return;
        }

        // Check if it's a network error that might be retryable
        const isNetworkError =
          type === "networkError" ||
          details === "manifestLoadError" ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("connection");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (code 4): no valid source — permanent, never retry.
        // "The element has no supported sources" = wrong/missing playback ID.
        const isNoSourceError =
          details.toLowerCase().startsWith("code 4") ||
          msg.toLowerCase().includes("no supported sources") ||
          msg.toLowerCase().includes("src_not_supported");

        // Decode/parse errors (e.g. demuxer_error_could_not_parse, MEDIA_ERR_DECODE)
        // Often transient MSE/CDN issues; retrying player.load() commonly recovers.
        const isDecodeError =
          !isNoSourceError && (
            msg.toLowerCase().includes("demuxer") ||
            msg.toLowerCase().includes("could_not_parse") ||
            msg.toLowerCase().includes("decode") ||
            msg.toLowerCase().includes("not supported") ||
            details.toLowerCase().includes("demuxer") ||
            details.toLowerCase().includes("decode") ||
            details.toLowerCase().includes("parse")
          );

        const isInvalid =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          (typeof url === "string" &&
            url.includes("stream.mux.com") &&
            url.includes("404")) ||
          isNoSourceMux;

        // Join flow: priorizar fallback a inglés antes que retries (siempre debe haber video)
        // Si no es join flow y es network/decode error, retry primero
        if (
          !isJoinFlowService &&
          (isNetworkError || isDecodeError) &&
          !isNoSourceError &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          !fatal
        ) {
          if (import.meta.env.DEV) {
            console.log(
              `[MuxVideoPlayerWithCTA] ${isDecodeError ? "Decode" : "Network"} error detected, retrying (${
                retryCount + 1
              }/${MAX_RETRIES})...`,
            );
          }
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);

          // Retry after a delay
          setTimeout(
            () => {
              const player = playerRef.current;
              if (player && typeof player.load === "function") {
                player.load();
              }
            },
            1000 * (retryCount + 1),
          ); // Exponential backoff
          return;
        }

        // Recoverable MSE/HLS errors (p. ej. bufferAppendError): un load() suele bastar.
        // hls.js marca algunos como fatal; igual permitimos reintentos acotados.
        if (
          !isJoinFlowService &&
          type.toLowerCase() === "mediaerror" &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          (!fatal || isBufferAppendMux)
        ) {
          if (import.meta.env.DEV) {
            console.warn(
              `[MuxVideoPlayerWithCTA] Recoverable mediaError, silent reload (${retryCount + 1}/${MAX_RETRIES}):`,
              msg.slice(0, 120),
            );
          }
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);
          setTimeout(() => {
            const player = playerRef.current;
            if (player && typeof player.load === "function") {
              player.load();
            }
          }, 500);
          return;
        }

        // DualCTA: el padre rota playback-id; evita doble rotación + spam PostHog.
        if (parentOwnsPlaybackRotation) {
          reportFatalErrorToParent(msg || type || "video_error");
          return;
        }

        // Log producción: error de video detectado
        const normalizedMsg = msg.toLowerCase();
        const normalizedDetails = details.toLowerCase();
        const isTokenError =
          normalizedMsg.includes("token") ||
          normalizedMsg.includes("unauthorized") ||
          normalizedMsg.includes("forbidden") ||
          normalizedMsg.includes("jwt") ||
          normalizedMsg.includes("signature") ||
          normalizedMsg.includes("access denied") ||
          normalizedMsg.includes("401") ||
          normalizedMsg.includes("403") ||
          normalizedDetails.includes("token") ||
          normalizedDetails.includes("unauthorized") ||
          normalizedDetails.includes("forbidden");
        logVideoErrorProduction({
          event: "video_error",
          country: effectiveCountry,
          service: service ?? undefined,
          errorType: type || (isNetworkError ? "network" : "other"),
          errorMessage: msg.slice(0, 200),
          playbackId: effectivePlaybackId,
          isNetworkError,
          isTokenError,
        });

        // channel_invite (telegram groups): NUNCA bloquear - siempre permitir CTA
        if (isJoinFlowService) {
          if (!hasTriedFallbackRef.current) {
            const englishPlaybackId = getFallbackPlaybackIdForErrors();
            if (englishPlaybackId && englishPlaybackId.trim() !== "") {
              logVideoErrorProduction({
                event: "fallback_attempted",
                country: effectiveCountry,
                service: service ?? undefined,
                errorType: type || (isNetworkError ? "network" : "other"),
                playbackId: effectivePlaybackId,
                fallbackAttempted: true,
              });
              hasTriedFallbackRef.current = true;
              setNetworkErrorFallbackPlaybackId(englishPlaybackId);
            } else {
              logVideoErrorProduction({
                event: "fallback_failed",
                country: effectiveCountry,
                service: service ?? undefined,
                errorMessage: "no fallback playback ID available",
                fallbackAttempted: false,
              });
            }
          } else {
            logVideoErrorProduction({
              event: "fallback_failed",
              country: effectiveCountry,
              service: service ?? undefined,
              errorMessage: "fallback video also failed",
              playbackId: effectivePlaybackId,
              fallbackAttempted: true,
            });
          }
          setIsLoading(false);
          setErrorMessage("");
          setHasError(false);
          return;
        }

        // Para network/token/decode errors (no join flow): intentar fallback si aplica
        if ((isNetworkError || isTokenError || isDecodeError) && !isInvalid) {
          const fallbackPlaybackId = getFallbackPlaybackIdForErrors();
          const manualAltRotation =
            Boolean(manualPlaybackId?.trim()) &&
            !service &&
            (fallbackPlaybackIds?.length ?? 0) > 0;
          const canAttemptFallback =
            Boolean(fallbackPlaybackId?.trim()) &&
            (manualAltRotation || !hasTriedFallbackRef.current);
          if (canAttemptFallback && fallbackPlaybackId) {
            logVideoErrorProduction({
              event: "fallback_attempted",
              country: effectiveCountry,
              service: service ?? undefined,
              errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
              fallbackAttempted: true,
            });
            if (!manualAltRotation) {
              hasTriedFallbackRef.current = true;
            } else {
              triedFallbackPlaybackIdsRef.current.add(effectivePlaybackId);
            }
            setNetworkErrorFallbackPlaybackId(fallbackPlaybackId);
            setIsLoading(false);
            setErrorMessage("");
            return;
          }
          // Sin fallback disponible (caso típico: manualPlaybackId standalone como VIP/sorpresa).
          // No bloquear la UI: el thumbnail y el CTA siguen visibles para no perder conversión.
          logVideoErrorProduction({
            event: "fallback_failed",
            country: effectiveCountry,
            service: service ?? undefined,
            errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
            errorMessage: "no fallback playback ID available; keeping CTA visible",
            playbackId: effectivePlaybackId,
            fallbackAttempted: false,
          });
          setIsLoading(false);
          setErrorMessage("");
          setHasError(false);
          return;
        }

        // Solo mostrar error blocking para otros servicios (no join flow)
        setHasError(true);
        setIsLoading(false);
        setErrorMessage(
          isInvalid
            ? "This video is not available. The playback ID may be invalid or the video has been removed."
            : "Video could not be loaded. Please try again later.",
        );
        reportFatalErrorToParent(msg || type || "video_error");
      } catch (err) {
        console.error("[MuxVideoPlayerWithCTA] Error in handleError:", err);
        // Don't set error state for unexpected errors in error handler
        // This prevents cascading errors from breaking the component
      }
    },
    [
      retryCount,
      service,
      manualPlaybackId,
      fallbackPlaybackIds,
      effectiveCountry,
      effectivePlaybackId,
      getFallbackPlaybackIdForErrors,
      parentOwnsPlaybackRotation,
      reportFatalErrorToParent,
    ],
  );

  /** Mux onError puede ser CustomEvent: `detail` es un objeto con type/details/fatal, no un string. */
  function extractMuxPlayerErrorPayload(
    error: unknown,
  ): {
    message: string;
    type: string;
    details: string;
    url?: string;
    fatal?: boolean;
  } | null {
    if (!error || typeof error !== "object") return null;
    const e = error as Record<string, unknown>;
    if (typeof e.detail === "string" && e.detail.trim() !== "") {
      const s = e.detail.trim();
      return { message: s, type: "mediaError", details: s, fatal: false };
    }
    const raw =
      e.detail && typeof e.detail === "object"
        ? (e.detail as Record<string, unknown>)
        : e;
    const message = String(raw.message ?? "");
    const type = String(raw.type ?? "");
    const details = String(raw.details ?? "");
    const url = typeof raw.url === "string" ? raw.url : undefined;
    const fatal = Boolean(raw.fatal);
    if (!message && !type && !details && !url) return null;
    return { message, type, details, url, fatal };
  }

  // Wrapper for MuxPlayer onError to validate it's a video error
  const handleMuxPlayerError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      if (!error) {
        return;
      }

      const payload = extractMuxPlayerErrorPayload(error);

      let errorStr = "";
      try {
        errorStr = JSON.stringify(error).toLowerCase();
      } catch {
        errorStr = String(error).toLowerCase();
      }

      const combinedStr = payload
        ? `${errorStr} ${payload.message} ${payload.type} ${payload.details} ${payload.url ?? ""}`.toLowerCase()
        : errorStr;

      const hasVideoIndicators =
        combinedStr.includes("video") ||
        combinedStr.includes("playback") ||
        combinedStr.includes("stream") ||
        combinedStr.includes("mux") ||
        combinedStr.includes("manifest") ||
        combinedStr.includes("network") ||
        combinedStr.includes("tunnel") ||
        combinedStr.includes("connection") ||
        combinedStr.includes("mediaerror") ||
        combinedStr.includes("invalid") ||
        combinedStr.includes("not supported") ||
        combinedStr.includes("bufferappend") ||
        (payload?.type === "mediaError" || payload?.type === "networkError");

      if (!hasVideoIndicators) {
        console.debug(
          "[MuxVideoPlayerWithCTA] Ignoring non-video error from MuxPlayer:",
          error,
        );
        return;
      }

      if (payload) {
        handleError(payload);
      } else {
        handleError(error);
      }
    },
    [handleError],
  );

  useEffect(() => {
    if (!effectivePlaybackId) return;

    const onVideoError = (event: Event) => {
      const target = event.target as HTMLVideoElement;
      // Only handle errors from actual video elements
      if (!target || target.tagName !== "VIDEO") {
        return;
      }

      const err = target.error;
      if (err && err.code !== undefined) {
        // Only handle actual video errors (MEDIA_ERR_* codes)
        // MEDIA_ERR_ABORTED = 1 (tab hide / load() interrupt) — not a user-facing failure.
        if (err.code === 1) {
          return;
        }
        // MEDIA_ERR_NETWORK = 2, MEDIA_ERR_DECODE = 3, MEDIA_ERR_SRC_NOT_SUPPORTED = 4
        if (err.code >= 2 && err.code <= 4) {
          const errorDetails = {
            message: err.message || `Video error code ${err.code}`,
            type: "mediaError",
            details: `Code ${err.code}: ${
              err.message || "Unknown video error"
            }`,
            url: target.src || target.currentSrc,
          };

          // Log detailed error info
          logVideoEvent({
            event: err.code === 2 ? "network_error" : "video_load_failed",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "mediaError",
            errorMessage: err.message || `Video error code ${err.code}`,
            errorCode: err.code,
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: err.code === 2,
          });

          handleError(errorDetails);
        }
      } else {
        // Only handle network errors if they're from Mux domains
        const src = target.src || target.currentSrc || "";
        if (src.includes("mux.com") || src.includes("stream.mux.com")) {
          // Log network error
          logVideoEvent({
            event: "network_error",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "networkError",
            errorMessage: "Video could not be loaded",
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: true,
          });

          handleError({
            type: "networkError",
            message: "Video could not be loaded",
            details: "Network or loading error",
            url: src,
          });
        }
      }
    };

    const setup = (): (() => void) | undefined => {
      const p = playerRef.current;
      if (!p) return undefined;
      let el: HTMLVideoElement | null = null;
      if (
        typeof (p as { querySelector?: (s: string) => HTMLElement | null })
          .querySelector === "function"
      ) {
        el = (
          p as { querySelector: (s: string) => HTMLVideoElement | null }
        ).querySelector("video");
      }
      if (!el && (p as { shadowRoot?: ShadowRoot }).shadowRoot) {
        el = (p as { shadowRoot: ShadowRoot }).shadowRoot.querySelector(
          "video",
        );
      }
      if (!el && (p as { media?: HTMLVideoElement }).media) {
        el = (p as { media: HTMLVideoElement }).media;
      }
      if (el) {
        el.addEventListener("error", onVideoError);
        return () => el?.removeEventListener("error", onVideoError);
      }
      return undefined;
    };

    let cleanup2: (() => void) | undefined;
    const cleanup1 = setup();
    const t = setTimeout(() => {
      cleanup2 = setup();
    }, 500);
    return () => {
      cleanup1?.();
      cleanup2?.();
      clearTimeout(t);
    };
  }, [effectivePlaybackId, handleError, effectiveCountry, service]);

  // Stall recovery: Mux Player's onStalled/onWaiting only emit telemetry. On
  // flaky mobile networks (~10/24h of `video_stalled` events with no recovery,
  // per PostHog) the user gets stuck on a frozen frame indefinitely once
  // playback has started — the existing `video_shown_thumbnail_only` watchdog
  // only fires before first play. Here we listen on the underlying HTMLVideoElement
  // and progressively kick: (1) nudge currentTime to flush the buffer, (2) full
  // load() + play(), (3) escalate to handleError so the user lands on the
  // fallback CTA instead of a dead spinner.
  useEffect(() => {
    if (!effectivePlaybackId) return;

    const RECOVERY_DELAY_MS = 3500;
    const HAVE_FUTURE_DATA = 3;
    let attempts = 0;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

    const attachToVideo = (
      videoEl: HTMLVideoElement,
    ): (() => void) | undefined => {
      const clearRecovery = () => {
        if (recoveryTimer != null) {
          clearTimeout(recoveryTimer);
          recoveryTimer = null;
        }
      };

      const onPlaying = () => {
        clearRecovery();
        attempts = 0;
      };

      const scheduleRecovery = () => {
        if (recoveryTimer != null) return; // already armed
        recoveryTimer = setTimeout(() => {
          recoveryTimer = null;
          const stillStalled = videoEl.readyState < HAVE_FUTURE_DATA;
          if (!stillStalled) return;

          attempts += 1;
          logVideoEvent({
            event: "video_stall_recovery",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            attempt: attempts,
            readyState: videoEl.readyState,
            networkState: videoEl.networkState,
            currentTime: Number(videoEl.currentTime.toFixed(3)),
          });

          try {
            if (attempts === 1) {
              const t = videoEl.currentTime;
              videoEl.currentTime = Math.max(0, t - 0.05);
              const p = videoEl.play();
              if (p && typeof (p as Promise<void>).catch === "function") {
                (p as Promise<void>).catch(() => undefined);
              }
            } else if (attempts === 2) {
              videoEl.load();
              const p = videoEl.play();
              if (p && typeof (p as Promise<void>).catch === "function") {
                (p as Promise<void>).catch(() => undefined);
              }
            } else {
              handleError({
                type: "stallExhausted",
                message: "Video stalled and could not recover",
                details: `attempts=${attempts} readyState=${videoEl.readyState} networkState=${videoEl.networkState}`,
                url: `https://stream.mux.com/${effectivePlaybackId}.m3u8`,
              });
            }
          } catch {
            // ignore — recovery is best-effort
          }
        }, RECOVERY_DELAY_MS);
      };

      videoEl.addEventListener("stalled", scheduleRecovery);
      videoEl.addEventListener("waiting", scheduleRecovery);
      videoEl.addEventListener("playing", onPlaying);
      videoEl.addEventListener("canplay", onPlaying);

      return () => {
        videoEl.removeEventListener("stalled", scheduleRecovery);
        videoEl.removeEventListener("waiting", scheduleRecovery);
        videoEl.removeEventListener("playing", onPlaying);
        videoEl.removeEventListener("canplay", onPlaying);
        clearRecovery();
      };
    };

    // Same shadow-DOM-aware lookup as the error effect above. Mux Player
    // mounts the <video> inside its shadowRoot or as `.media`.
    const setup = (): (() => void) | undefined => {
      const p = playerRef.current;
      if (!p) return undefined;
      let el: HTMLVideoElement | null = null;
      const ps = p as {
        querySelector?: (s: string) => HTMLElement | null;
        shadowRoot?: ShadowRoot;
        media?: HTMLVideoElement;
      };
      if (typeof ps.querySelector === "function") {
        el = ps.querySelector("video") as HTMLVideoElement | null;
      }
      if (!el && ps.shadowRoot) {
        el = ps.shadowRoot.querySelector("video") as HTMLVideoElement | null;
      }
      if (!el && ps.media) {
        el = ps.media;
      }
      return el ? attachToVideo(el) : undefined;
    };

    let cleanup2: (() => void) | undefined;
    const cleanup1 = setup();
    const retryTimer = setTimeout(() => {
      cleanup2 = setup();
    }, 500);
    return () => {
      cleanup1?.();
      cleanup2?.();
      clearTimeout(retryTimer);
      if (recoveryTimer != null) clearTimeout(recoveryTimer);
    };
  }, [
    effectivePlaybackId,
    handleError,
    effectiveCountry,
    service,
  ]);

  // Helper function to get video element from player
  const getVideoElement = useCallback(
    (
      player: {
        querySelector?: (s: string) => HTMLElement | null;
        shadowRoot?: ShadowRoot;
        media?: HTMLVideoElement;
      } | null,
    ): HTMLVideoElement | null => {
      if (!player) return null;

      // Try different methods to get the video element
      if (typeof player.querySelector === "function") {
        const el = player.querySelector("video") as HTMLVideoElement | null;
        if (el) return el;
      }

      if (player.shadowRoot) {
        const el = player.shadowRoot.querySelector("video");
        if (el) return el;
      }

      if (player.media) {
        return player.media;
      }

      return null;
    },
    [],
  );

  const revealWatcherLuckToast = useCallback(() => {
    if (
      !stripeWatcherLuckEligible ||
      stripeWatcherLuckPercent <= 0 ||
      watcherLuckToastShownRef.current
    ) {
      return;
    }
    if (checkoutStateRef.current !== "VIDEO_PREVIEW") return;

    watcherLuckToastShownRef.current = true;

    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }

    setShowWatcherLuckBanner(true);
    if (watcherLuckToastTimerRef.current !== null) {
      window.clearTimeout(watcherLuckToastTimerRef.current);
    }
    watcherLuckToastTimerRef.current = window.setTimeout(() => {
      setShowWatcherLuckBanner(false);
      watcherLuckToastTimerRef.current = null;
    }, WATCHER_LUCK_TOAST_MS);
  }, [stripeWatcherLuckEligible, stripeWatcherLuckPercent]);

  const onPlaybackTelemetryRef = useRef(onPlaybackTelemetry);
  useEffect(() => {
    onPlaybackTelemetryRef.current = onPlaybackTelemetry;
  }, [onPlaybackTelemetry]);

  const playbackTelemetryThrottleRef = useRef(0);

  const emitPlaybackTelemetry = useCallback(
    (reason: "timeupdate" | "pause" | "ended") => {
      const cb = onPlaybackTelemetryRef.current;
      if (!cb) return;
      const now = Date.now();
      if (reason === "timeupdate") {
        if (now - playbackTelemetryThrottleRef.current < 4000) return;
        playbackTelemetryThrottleRef.current = now;
      }
      const p = playerRef.current;
      if (!p) return;
      let currentTimeSeconds = 0;
      let durationSeconds: number | null = null;
      const v = getVideoElement(p);
      if (v) {
        const ct = Number(v.currentTime);
        const d = Number(v.duration);
        currentTimeSeconds = Number.isFinite(ct) ? ct : 0;
        durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
      } else {
        const ct = (p as { currentTime?: number }).currentTime;
        const d = (p as { duration?: number }).duration;
        currentTimeSeconds =
          typeof ct === "number" && Number.isFinite(ct) ? ct : 0;
        durationSeconds =
          typeof d === "number" && Number.isFinite(d) && d > 0 ? d : null;
      }
      cb({
        currentTimeSeconds,
        durationSeconds,
        playbackId: effectivePlaybackId,
        reason,
      });
    },
    [getVideoElement, effectivePlaybackId],
  );

  const handleTimeUpdateForTelemetry = useCallback(() => {
    emitPlaybackTelemetry("timeupdate");
    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current
    ) {
      const now = Date.now();
      if (now - watcherLuckTUThrottleRef.current < 650) return;
      watcherLuckTUThrottleRef.current = now;
      try {
        const p = playerRef.current;
        if (!p) return;
        let ct = 0;
        const v = getVideoElement(p);
        if (v && Number.isFinite(v.currentTime)) {
          ct = v.currentTime;
        } else {
          const raw = (p as { currentTime?: number }).currentTime;
          ct = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
        }
        if (ct >= WATCHER_LUCK_MIN_PLAYHEAD_SEC) {
          revealWatcherLuckToast();
        }
      } catch {
        /* ignore */
      }
    }
  }, [
    emitPlaybackTelemetry,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    getVideoElement,
    revealWatcherLuckToast,
  ]);

  const handleCanPlay = useCallback(() => {
    setMuxCanPlayBuffered(true);
    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Video can play - setting volume");
    }

    // Log successful video load
    logVideoEvent({
      event: "video_load_success",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });

    // Set volume when video can play (before it starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
        // No cerramos el loading aquí: el overlay sigue hasta `play` (o botón manual / timeout).
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in canplay:", error);
    }
  }, [getVideoElement, effectiveCountry, service, effectivePlaybackId]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    // NOTE: `play` fires when the browser ATTEMPTS playback — the first frame
    // may not have rendered yet. We used to set `hasPlaybackStarted = true`
    // here, which cancelled the load watchdog prematurely. On the iOS / FB
    // in-app webview cohort, that combined with a `DEMUXER_ERROR_COULD_NOT_PARSE`
    // crash later on (HLS manifest / segment unparseable for the device's
    // codec) left the user stuck on a stalled spinner with no fallback CTA.
    // Real session 2026-06-01 23:57:27 → 23:57:47 (UK / Facebook Android IAB,
    // OnePlus DE2118, Android 12): play fired → watchdog cancelled → 20 s
    // later demuxer crashed → user bounced. `hasPlaybackStarted` now flips in
    // `handlePlaying` instead, where actual frame rendering is confirmed.
    setIsLoading(false); // Hide loading when video starts playing
    setShowPlayButton(false); // Hide play button when video starts playing

    // Log successful video play
    logVideoEvent({
      event: "video_play_success",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });

    // Set volume when video starts playing (safe - video is already playing)
    try {
      const p = playerRef.current;
      if (p) {
        const videoElement = getVideoElement(p);
        if (videoElement && "volume" in videoElement) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      // Non-critical error, just log it
      console.debug(
        "[MuxVideoPlayerWithCTA] Error setting volume on play:",
        error,
      );
    }

    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Video is playing");
    }
    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current &&
      watcherLuckWallTimerRef.current === null
    ) {
      watcherLuckWallTimerRef.current = window.setTimeout(() => {
        watcherLuckWallTimerRef.current = null;
        revealWatcherLuckToast();
      }, WATCHER_LUCK_FALLBACK_WALL_MS);
    }
    onPlay?.();
  }, [
    onPlay,
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    revealWatcherLuckToast,
  ]);

  const dismissWatcherLuckBanner = useCallback(() => {
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    emitPlaybackTelemetry("pause");
  }, [emitPlaybackTelemetry]);

  /**
   * Fires only once real frame rendering has begun (HTMLMediaElement `playing`
   * event, as opposed to `play` which just signals "playback was attempted").
   * This is the right signal for "the user is actually seeing video" — used to
   * cancel the load watchdog in DualCTAVideoLanding. If the player attempts
   * play and then crashes (DEMUXER error, codec mismatch, network drop), the
   * watchdog stays alive and the tap-to-play fallback can still render.
   */
  const handlePlaying = useCallback(() => {
    setHasPlaybackStarted(true);
    onPlayingProp?.();
  }, [onPlayingProp]);

  // Handler for manual play button click — user gesture allows unmuted play.
  // Still fall back to muted + AbortError retry so tap-to-play recovers when
  // autoplay left the element mid-abort.
  const handleManualPlay = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player || typeof player.play !== "function") return;

      const videoElement = getVideoElement(player);
      if (videoElement) {
        videoElement.muted = false;
        if ("volume" in videoElement) {
          videoElement.volume = 1.0;
        }
        setIsMuted(false);
      }

      const playMuted = () => {
        if (videoElement) {
          videoElement.muted = true;
        }
        setIsMuted(true);
        return player
          .play()
          .then(() => {
            setIsPlaying(true);
            setShowPlayButton(false);
            setShowMuteIndicator(true);
          })
          .catch((mutedErr: unknown) => {
            const name =
              mutedErr instanceof Error
                ? mutedErr.name
                : String((mutedErr as { name?: string })?.name ?? "");
            if (name === "AbortError") {
              return new Promise<void>((resolve) => setTimeout(resolve, 120)).then(
                () => {
                  if (videoElement) videoElement.muted = true;
                  return player.play().then(() => {
                    setIsPlaying(true);
                    setShowPlayButton(false);
                    setShowMuteIndicator(true);
                  });
                },
              );
            }
            throw mutedErr;
          })
          .catch(() => {
            console.error("[MuxVideoPlayerWithCTA] Muted play also failed");
          });
      };

      player
        .play()
        .then(() => {
          setIsPlaying(true);
          setShowPlayButton(false);
          setShowMuteIndicator(false);
          if (import.meta.env.DEV) {
            console.log(
              "[MuxVideoPlayerWithCTA] Manual play with sound succeeded",
            );
          }
        })
        .catch((error) => {
          console.warn("[MuxVideoPlayerWithCTA] Manual play failed:", error);
          void playMuted();
        });
    } catch (error) {
      console.error(
        "[MuxVideoPlayerWithCTA] Error in handleManualPlay:",
        error,
      );
    }
  }, [getVideoElement]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    emitPlaybackTelemetry("ended");
    onEnded?.();
  }, [onEnded, emitPlaybackTelemetry]);

  // Toggle mute/unmute - Instagram-like tap to unmute on mobile
  const toggleMute = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;

      const videoElement = getVideoElement(player);
      if (videoElement) {
        const newMutedState = !isMuted;
        videoElement.muted = newMutedState;
        setIsMuted(newMutedState);

        // If unmuting, set volume to 100%
        if (!newMutedState && "volume" in videoElement) {
          videoElement.volume = 1.0;
        }

        if (import.meta.env.DEV) {
          console.log(
            `[MuxVideoPlayerWithCTA] ${newMutedState ? "Muted" : "Unmuted"} video`,
          );
        }
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in toggleMute:", error);
    }
  }, [isMuted, getVideoElement]);

  const togglePlayPause = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;
      if (
        typeof player.play !== "function" ||
        typeof player.pause !== "function"
      )
        return;
      if (isPlaying) {
        player.pause();
        setIsPlaying(false);
      } else {
        player.play().catch((error) => {
          console.debug(
            "[MuxVideoPlayerWithCTA] Play failed in toggle:",
            error,
          );
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in togglePlayPause:", error);
    }
  }, [isPlaying]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const touches = e.changedTouches;
      if (!touches?.length) {
        touchStartRef.current = null;
        return;
      }
      const t = touches[0];
      const target = document.elementFromPoint(
        t.clientX,
        t.clientY,
      ) as HTMLElement | null;
      if (
        target?.closest(".action-button-container") ||
        target?.closest(".mux-cta-close-btn")
      ) {
        touchStartRef.current = null;
        return;
      }
      const dx = Math.abs(t.clientX - touchStartRef.current.x);
      const dy = Math.abs(t.clientY - touchStartRef.current.y);
      const dt = Date.now() - touchStartRef.current.time;
      touchStartRef.current = null;

      // Only respond to taps (small movement, short duration)
      if (dx < 10 && dy < 10 && dt < 300) {
        // If video is playing: tap to toggle mute
        // This works for all devices (mobile, desktop, Instagram)
        if (isPlaying) {
          toggleMute();
        } else {
          togglePlayPause();
        }
      }
    },
    [togglePlayPause, toggleMute, isPlaying],
  );

  const handleLoadedMetadata = useCallback(() => {
    setHasReceivedMuxMetadata(true);
    // Log metadata loaded (video info received)
    logVideoEvent({
      event: "video_load_start",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });
    onReady?.();
    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Metadata loaded - setting volume");
    }

    // Set volume when metadata loads (before playback starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in loadedMetadata:", error);
    }
  }, [
    onReady,
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
  ]);

  const handleMuxLoadedData = useCallback(() => {
    setMuxHasLoadedData(true);
  }, []);

  // Show mute indicator when video is playing and muted
  // Keep visible until user unmutes (don't auto-hide)
  useEffect(() => {
    if (isPlaying && isMuted) {
      setShowMuteIndicator(true);
    } else {
      setShowMuteIndicator(false);
    }
  }, [isPlaying, isMuted]);

  // Handle autoplay.
  // Mobile + IABs almost always block unmuted autoplay (NotAllowedError). Trying
  // unmuted first then muted causes AbortError races (pause interrupts the second
  // play()) → player stuck, DualCTA soft-stall video_stuck_not_playing.
  // Strategy: muted-first on mobile/IG/Android/iOS; desktop still tries sound first.
  useEffect(() => {
    if (autoPlay === false) return;

    const player = playerRef.current;
    if (!player) return;

    let cancelled = false;
    let autoplayAttempted = false;
    // All mobile + social IABs: never waste a frame on doomed unmuted autoplay.
    const restrictedAutoplayContext =
      isInstagram || isMobile || isAndroid || isIOS;
    const tryUnmutedFirst =
      (autoPlay === "any" || preferAutoplayWithSound) &&
      !restrictedAutoplayContext &&
      autoPlay !== "muted";

    const applyMuted = () => {
      const videoElement = getVideoElement(player);
      if (videoElement) {
        videoElement.muted = true;
      }
      try {
        if (player && typeof player === "object" && "muted" in player) {
          (player as { muted: boolean }).muted = true;
        }
      } catch {
        /* ignore */
      }
      setIsMuted(true);
    };

    const playMutedWithRetry = async (): Promise<boolean> => {
      if (typeof player.play !== "function") return false;
      applyMuted();
      try {
        await player.play();
        if (cancelled) return false;
        setShowPlayButton(false);
        setShowMuteIndicator(true);
        if (import.meta.env.DEV) {
          console.log(
            "[MuxVideoPlayerWithCTA] Autoplay muted succeeded - showing audio prompt",
          );
        }
        return true;
      } catch (mutedError) {
        const name =
          mutedError instanceof Error
            ? mutedError.name
            : String((mutedError as { name?: string })?.name ?? "");
        // Concurrent load/pause aborts the play() promise — one quiet retry.
        if (name === "AbortError" && !cancelled) {
          await new Promise((r) => setTimeout(r, 150));
          if (cancelled) return false;
          applyMuted();
          try {
            await player.play();
            if (cancelled) return false;
            setShowPlayButton(false);
            setShowMuteIndicator(true);
            if (import.meta.env.DEV) {
              console.log(
                "[MuxVideoPlayerWithCTA] Autoplay muted succeeded after AbortError retry",
              );
            }
            return true;
          } catch (retryErr) {
            console.debug(
              "[MuxVideoPlayerWithCTA] Muted autoplay retry failed:",
              retryErr,
            );
          }
        } else {
          console.debug(
            "[MuxVideoPlayerWithCTA] Muted autoplay also failed:",
            mutedError,
          );
        }
        if (!cancelled) {
          setIsLoading(false);
          setShowPlayButton(true);
        }
        return false;
      }
    };

    const tryPlay = async () => {
      if (autoplayAttempted || cancelled) return;
      autoplayAttempted = true;

      if (tryUnmutedFirst) {
        try {
          const videoElement = getVideoElement(player);
          if (videoElement) {
            videoElement.muted = false;
            if ("volume" in videoElement) videoElement.volume = 1.0;
          }

          if (typeof player.play === "function") {
            await player.play();
            if (cancelled) return;
            setShowPlayButton(false);
            setIsMuted(false);
            setShowMuteIndicator(false);
            if (import.meta.env.DEV) {
              console.log(
                "[MuxVideoPlayerWithCTA] Autoplay with sound succeeded",
              );
            }
            return;
          }
        } catch (error) {
          console.debug(
            "[MuxVideoPlayerWithCTA] Autoplay with sound failed:",
            error,
          );
          // Let the aborted unmuted play settle before starting muted.
          await new Promise((r) => setTimeout(r, 50));
          if (cancelled) return;
        }
      }

      await playMutedWithRetry();
    };

    const initialDelay = restrictedAutoplayContext ? 80 : 50;
    const timeout = setTimeout(() => void tryPlay(), initialDelay);

    // Give muted autoplay more time on mobile before forcing the play button
    // (slow CN / 3G often needs >1.5s for first segment).
    const checkDelay = isMobileOrInstagram ? 3500 : 2000;
    const checkPlaybackTimeout = setTimeout(() => {
      if (cancelled) return;
      const videoElement = getVideoElement(player);
      if (videoElement && videoElement.paused && !hasPlaybackStartedRef.current) {
        setIsLoading(false);
        setShowPlayButton(true);
      }
    }, checkDelay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      clearTimeout(checkPlaybackTimeout);
    };
    // Do NOT depend on isLoading — flipping loading re-ran this effect, aborted
    // an in-flight play(), and caused AbortError stuck states in prod (CN DualCTA).
  }, [
    effectivePlaybackId,
    getVideoElement,
    isMobileOrInstagram,
    autoPlay,
    preferAutoplayWithSound,
    isInstagram,
    isIOS,
    isAndroid,
    isMobile,
  ]);

  /**
   * One-time Stripe PaymentIntent (packs / Elements) — NOT Dual CTA VIP MRR.
   * Dual CTA VIP membership is MRR via parent `onCTAClick` → startVipMembershipVideoCheckout
   * → /api/create-subscription-checkout (mode=subscription + DataFast metadata).
   * Price display for Dual CTA list/mo: src/lib/dualCtaPriceDisplay.ts
   */
  const createPaymentIntent = useCallback(
    async (packId: string, email?: string) => {
      setCheckoutError(null);

      // Early recovery capture for Dual CTA / membership flows when email provided in Mux CTA
      if (email && email.includes("@")) {
        try {
          const isDual = window.location.pathname.includes("dual") || window.location.pathname === "/" || window.location.pathname.includes("invitacion");
          if (isDual || packId.includes("vip") || packId.includes("membership")) {
            const cc = effectiveCountry || "US";
            persistDualCtaCheckoutEmail({
              email,
              country: cc,
              flowName: isDual ? "dual_cta_membership" : "membership",
              provider: cc === "AR" ? "mercadopago" : "stripe",
              amount: 0, // will be filled by server
              currency: cc === "AR" ? "ARS" : "USD",
              planTier: "vip",
            });
          }
        } catch {}
      }

      // ── ARGENTINA: VIP/membership → VITE_MP_CHECKOUT_VIP_AR; channel_invite → Basic ──
      if (effectiveCountry === "AR") {
        const isVipPack =
          packId.includes("vip") ||
          packId.includes("membership") ||
          packId.includes("exclusive") ||
          packId === "premium" ||
          packId === "dual_cta";
        if (isVipPack) {
          const mpUrl = getMercadoPagoCheckoutVipArUrl();
          if (!mpUrl) {
            trackStripePayFailurePostHog({
              stage: "flow_blocked",
              flow_surface: "mux_video_cta",
              failure_kind: "mp_vip_ar_missing_url",
              pack_id: packId,
              error_message: "Checkout VIP para Argentina no disponible",
            });
            setCheckoutError("Checkout VIP para Argentina no disponible. Contacta a soporte.");
            setCheckoutState("ERROR");
            return;
          }
          flushSync(() => setIsNavigatingAway(true));
          window.location.assign(mpUrl);
          return;
        }
        // Pack Basic (channel_invite) → MercadoPago Basic (canal); no es hero VIP.
        if (packId === "channel_invite") {
          const mpUrl = getMercadoPagoCheckoutBasicArUrl();
          if (!mpUrl) {
            trackStripePayFailurePostHog({
              stage: "flow_blocked",
              flow_surface: "mux_video_cta",
              failure_kind: "mp_basic_ar_missing_url",
              pack_id: packId,
              error_message: "Checkout Basic para Argentina no disponible",
            });
            setCheckoutError("Checkout Basic para Argentina no disponible. Contacta a soporte.");
            setCheckoutState("ERROR");
            return;
          }
          flushSync(() => setIsNavigatingAway(true));
          window.location.assign(mpUrl);
          return;
        }

        setCheckoutError(
          `El pack "${packId}" no está disponible para Argentina en este momento.`,
        );
        setCheckoutState("ERROR");
        trackStripePayFailurePostHog({
          stage: "flow_blocked",
          flow_surface: "mux_video_cta",
          failure_kind: "ar_pack_not_on_stripe",
          pack_id: packId,
        });
        console.error(`[createPaymentIntent] Pack "${packId}" not configured for Argentina`);
        return;
      }

      let stripeFailMeta: { http?: number; code?: string; kind?: string } = {};
      let amountCentsForTrack: number | undefined;

      try {
        // Para overridePackId (sorpresa, exclusive) priorizar static para que siempre exista precio y se genere el payment intent
        const packPricing =
          (overridePackId
            ? getPackPricingStatic(packId, effectiveCountry)
            : null) ??
          getPackPricing(packId, effectiveCountry) ??
          getPackPricingStatic(packId, effectiveCountry);
        if (!packPricing) {
          stripeFailMeta = { kind: "invalid_pack_id" };
          throw new Error("Invalid pack ID");
        }
        amountCentsForTrack = packPricing.amount;
        const apiUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}`
            : CREATE_PAYMENT_INTENT_ENDPOINT;
        // Custom Stripe: always USD regardless of country; amount is in USD cents
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: packPricing.amount,
            currency: "usd",
            packId: packId,
            buyerEmail: email || undefined, // Optional - Stripe will collect it in billing details
            source: window.location.pathname,
            country: effectiveCountry || undefined, // Fallback when server headers (IP country) are missing
            // For Dual CTA / membership, capture early for recovery
            ...(email ? { flow_name: "dual_cta_membership" } : {}),
            watcherLuckDiscountPercent: stripeWatcherLuckEligible
              ? stripeWatcherLuckPercent
              : 0,
            // DataFast attribution for one-time PI (not Dual CTA VIP MRR)
            metadata: {
              ...getDataFastStripeMetadata(),
              revenue_type: "one_time",
              product_type: "payment_intent",
            },
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          let errMsg = "Failed to create payment intent";
          let errCode: string | undefined;
          try {
            const errorData = JSON.parse(text) as {
              error?: string;
              message?: string;
              code?: string;
              redirect_url?: string;
            };

            errMsg = errorData.error || errorData.message || errMsg;
            errCode = errorData.code;
          } catch {
            // API returned non-JSON (e.g. 404 HTML)
            if (response.status === 404) {
              errMsg =
                "Payment API not found (404). Check deployment and /api/create-payment-intent.";
            } else if (response.status >= 500) {
              errMsg = `Server error (${response.status}). Check Vercel logs for STRIPE_SECRET_KEY and API errors.`;
            } else {
              errMsg = `API error (${response.status})`;
            }
          }
          stripeFailMeta = {
            http: response.status,
            code: errCode,
            kind: "http_error",
          };
          console.error(
            "[MuxVideoPlayerWithCTA] create-payment-intent failed:",
            response.status,
            text.slice(0, 200),
          );
          throw new Error(errMsg);
        }

        const data = await response.json();
        if (!data?.clientSecret) {
          stripeFailMeta = { kind: "missing_client_secret" };
          throw new Error("Invalid API response: missing clientSecret");
        }
        setClientSecret(data.clientSecret);
        setCheckoutState("CHECKOUT");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to initialize checkout";

        // Track payment intent creation error (GA + PostHog)
        trackButtonClickGA(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        if (
          typeof (
            window as unknown as {
              posthogSafeCapture?: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture === "function"
        ) {
          (
            window as unknown as {
              posthogSafeCapture: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture("payment_intent_error", {
            service,
            country: effectiveCountry,
            pathname: window.location.pathname,
            error_message: message.slice(0, 200),
            pack_id: packId,
            ...(slotKey ? { video_ab_slot_key: slotKey, video_ab_variant_label: slotVariantLabel || "" } : {}),
          });
        }

        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "mux_video_cta",
          http_status: stripeFailMeta.http,
          stripe_error_code: stripeFailMeta.code,
          failure_kind: stripeFailMeta.kind,
          error_message: message,
          pack_id: packId,
          amount_cents: amountCentsForTrack,
          currency: "usd",
        });

        setCheckoutError(message);
        setCheckoutState("ERROR");
      } finally {
        setIsCreatingPaymentIntent(false);
      }
    },
    [
      effectiveCountry,
      getPackPricing,
      overridePackId,
      service,
      stripeWatcherLuckEligible,
      stripeWatcherLuckPercent,
    ],
  );

  const activateCtaFromUser = useCallback(() => {
    try {
      trackMuxCheckoutClick({
        ...buildMuxPaywallCtx(),
        trigger: onCTAClick ? "video_overlay_open" : "mux_cta_impression",
        gateway:
          service === "channel_invite" || overridePackId
            ? "stripe"
            : "external_url",
      });

      // Parent-controlled flows (e.g. BioTemplatePremium) must always win.
      if (onCTAClick) {
        if (parentCtaLockRef.current) {
          return;
        }
        parentCtaLockRef.current = true;
        if (parentCtaUnlockTimerRef.current !== null) {
          clearTimeout(parentCtaUnlockTimerRef.current);
        }
        parentCtaUnlockTimerRef.current = window.setTimeout(() => {
          parentCtaLockRef.current = false;
          parentCtaUnlockTimerRef.current = null;
        }, 900);
        Promise.resolve(onCTAClick(ctaUrl || "")).catch((callbackError) => {
          console.error(
            "[MuxVideoPlayerWithCTA] onCTAClick callback failed:",
            callbackError,
          );
        });
        return;
      }
      const isCheckoutEnabled = Boolean(STRIPE_PUBLISHABLE_KEY);
      const isPurchasableService =
        service === "channel_invite" || Boolean(overridePackId);
      if (!ctaUrlValid && !(isCheckoutEnabled && isPurchasableService)) {
        console.warn(
          "[MuxVideoPlayerWithCTA] CTA URL is not valid and custom checkout not available",
        );
        return;
      }

      console.log("[MuxVideoPlayerWithCTA] CTA activation:", {
        service,
        isCheckoutEnabled,
        isPurchasableService,
        isMobile,
        willUseCustomCheckout:
          isCheckoutEnabled && isPurchasableService && !isMobile,
        willRedirectToCheckoutPage:
          isCheckoutEnabled && isPurchasableService && isMobile,
      });

      if (isCheckoutEnabled && isPurchasableService) {
        if (service === "channel_invite" && !overridePackId) {
          const cc = (effectiveCountry || "US").toUpperCase();
          // channel_invite AR = plan Basic (no VIP hero)
          const arMpUrl = cc === "AR" ? getMercadoPagoCheckoutBasicArUrl() : "";
          if (arMpUrl) {
            console.log(
              "[MuxVideoPlayerWithCTA] channel_invite AR - redirecting to MercadoPago preapproval",
            );
            trackMuxStripeRedirect({
              ...buildMuxPaywallCtx(),
              trigger: "mux_cta_impression",
              gateway: "mercadopago",
              redirectUrl: arMpUrl,
            });
            void flushPostHogBeforeNavigation(200);
            flushSync(() => setIsNavigatingAway(true));
            window.location.assign(arMpUrl);
            return;
          }
          if (cc === "AR") {
            trackStripePayFailurePostHog({
              stage: "flow_blocked",
              flow_surface: "mux_video_cta",
              failure_kind: "mp_ar_membership_missing_url",
              pack_id: effectivePackId,
              error_message: "Checkout MercadoPago para Argentina no configurado",
            });
            setCheckoutError(
              "Checkout para Argentina no disponible. Contacta a soporte.",
            );
            setCheckoutState("ERROR");
            return;
          }
          const checkoutTarget =
            cc === "UNKNOWN"
              ? `/checkout-channel-invite-router?pack=${effectivePackId}&country=${cc}&lang=${detectedLanguage}`
              : `/checkout-channel-invite?pack=${effectivePackId}&country=${cc}&lang=${detectedLanguage}`;
          console.log(
            "[MuxVideoPlayerWithCTA] channel_invite flow - redirecting to checkout",
            { checkoutTarget, country: cc },
          );
          trackMuxStripeRedirect({
            ...buildMuxPaywallCtx(),
            trigger: "mux_cta_impression",
            gateway: "internal_checkout",
            redirectUrl: checkoutTarget,
          });
          void flushPostHogBeforeNavigation(250);
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = checkoutTarget;
          return;
        }

        const buttonLabel = isMobile
          ? "Custom checkout start (mobile)"
          : "Custom checkout start (desktop)";
        trackButtonClickGA(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        if (
          typeof (
            window as unknown as {
              posthogSafeCapture?: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture === "function"
        ) {
          (
            window as unknown as {
              posthogSafeCapture: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture("custom_checkout_started", {
            service,
            country: effectiveCountry,
            pathname: window.location.pathname,
            is_mobile: isMobile,
            ...(slotKey ? { video_ab_slot_key: slotKey, video_ab_variant_label: slotVariantLabel || "" } : {}),
          });
        }

        if (isMobile) {
          console.log(
            "[MuxVideoPlayerWithCTA] Mobile - redirecting to checkout page",
          );
          const checkoutUrl = `/checkout-channel-invite?pack=${effectivePackId}&country=${effectiveCountry || "US"}&lang=${detectedLanguage}`;
          trackMuxStripeRedirect({
            ...buildMuxPaywallCtx(),
            trigger: "mux_cta_impression",
            gateway: "internal_checkout",
            redirectUrl: checkoutUrl,
          });
          void flushPostHogBeforeNavigation(250);
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = checkoutUrl;
          return;
        }

        console.log(
          "[MuxVideoPlayerWithCTA] Desktop - opening integrated checkout",
        );
        flushSync(() => setIsCreatingPaymentIntent(true));
        void createPaymentIntent(effectivePackId);
        return;
      }

      console.log(
        "[MuxVideoPlayerWithCTA] Falling back to URL navigation:",
        ctaUrl,
      );

      if (isCumpleDestination(ctaUrl)) {
        const workerCta = skipWorkerTemplateCta
          ? null
          : resolveWorkerDrivenCta();
        trackBirthdayCtaClick({
          source: "mux_video_cta",
          href: ctaUrl,
          label: ctaLabel,
          templateName: "MuxVideoPlayerWithCTA",
          elementId: "mux_video_cta_birthday",
          surface: "video_overlay",
          countryCode: effectiveCountry,
          workerVariant:
            workerCta?.mode === "worker" ? workerCta.variant : "birthday",
        });
      }

      const externalGateway =
        ctaUrl.includes("checkout.stripe.com") || ctaUrl.includes("stripe.com")
          ? "stripe"
          : ctaUrl.includes("mercadopago")
            ? "mercadopago"
            : "external_url";

      if (isInstagram) {
        trackMuxStripeRedirect({
          ...buildMuxPaywallCtx(),
          trigger: "mux_cta_impression",
          gateway: externalGateway,
          redirectUrl: ctaUrl,
        });
        flushSync(() => setIsNavigatingAway(true));
        if (interceptMercadoPagoCta(ctaUrl)) {
          setIsNavigatingAway(false);
          return;
        }
        window.location.href = ctaUrl;
        return;
      }
      if (openInNewTab) {
        const newWindow = window.open(
          ctaUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (!newWindow) {
          trackMuxStripeRedirect({
            ...buildMuxPaywallCtx(),
            trigger: "mux_cta_impression",
            gateway: externalGateway,
            redirectUrl: ctaUrl,
          });
          void flushPostHogBeforeNavigation(250);
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = ctaUrl;
        }
      } else {
        trackMuxStripeRedirect({
          ...buildMuxPaywallCtx(),
          trigger: "mux_cta_impression",
          gateway: externalGateway,
          redirectUrl: ctaUrl,
        });
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = ctaUrl;
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in activateCtaFromUser:", error);
      try {
        void flushPostHogBeforeNavigation(200);
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = ctaUrl;
      } catch (fallbackError) {
        console.error(
          "[MuxVideoPlayerWithCTA] Fallback navigation also failed:",
          fallbackError,
        );
        setIsNavigatingAway(false);
      }
    }
  }, [
    ctaUrl,
    ctaLabel,
    ctaUrlValid,
    openInNewTab,
    onCTAClick,
    isInstagram,
    service,
    createPaymentIntent,
    isMobile,
    effectiveCountry,
    detectedLanguage,
    effectivePackId,
    overridePackId,
    parentCtaLockRef,
    parentCtaUnlockTimerRef,
    skipWorkerTemplateCta,
    buildMuxPaywallCtx,
  ]);

  const handleCTAClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Checkout handlers - email is now collected by Stripe in billing details

  const handleCheckoutSuccess = useCallback(
    (paymentIntentId?: string, paymentIntentSecret?: string) => {
      // Track custom checkout success before redirect (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      if (
        typeof (
          window as unknown as {
            posthogSafeCapture?: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture === "function"
      ) {
        (
          window as unknown as {
            posthogSafeCapture: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture("custom_checkout_success", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          payment_intent_id: paymentIntentId ?? undefined,
          ...(slotKey ? { video_ab_slot_key: slotKey, video_ab_variant_label: slotVariantLabel || "" } : {}),
        });
      }

      // Determine language for thank you page
      const browserLang = navigator.language.toLowerCase();
      const isSpanish =
        browserLang.startsWith("es") || browserLang.includes("es-");
      const thankYouPath = isSpanish ? "/gracias" : "/thank-you";

      // Build redirect URL with payment_intent parameters (same format as Stripe redirect)
      const params = new URLSearchParams();
      if (paymentIntentId) {
        params.append("payment_intent", paymentIntentId);
      }
      if (paymentIntentSecret) {
        params.append("payment_intent_client_secret", paymentIntentSecret);
      }
      // Add redirect_status=succeeded to match Stripe's redirect format
      params.append("redirect_status", "succeeded");

      const redirectUrl = `${thankYouPath}?${params.toString()}`;

      // Redirect to thank you page
      flushSync(() => setIsNavigatingAway(true));
      window.location.href = redirectUrl;
    },
    [service, effectiveCountry],
  );

  const handleCheckoutError = useCallback(
    (error: string) => {
      // Track checkout error (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      if (
        typeof (
          window as unknown as {
            posthogSafeCapture?: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture === "function"
      ) {
        (
          window as unknown as {
            posthogSafeCapture: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture("custom_checkout_error", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          error_message: error.slice(0, 200),
          ...(slotKey ? { video_ab_slot_key: slotKey, video_ab_variant_label: slotVariantLabel || "" } : {}),
        });
      }

      trackStripePayFailurePostHog({
        stage: "payment_confirm",
        flow_surface: "mux_video_cta",
        failure_kind: "stripe_checkout_form",
        error_message: error,
        pack_id: effectivePackId,
        currency: "usd",
      });

      // Log error to console for debugging
      console.error("[MuxVideoPlayerWithCTA] Checkout error:", error);

      setCheckoutError(error);
      setCheckoutState("ERROR");
      setIsNavigatingAway(false);
    },
    [service, effectiveCountry, effectivePackId],
  );

  const handleBackToVideo = useCallback(() => {
    muxStripeJsUnavailableTrackedRef.current = false;
    setCheckoutState("VIDEO_PREVIEW");
    setClientSecret(null);
    setCheckoutError(null);
    setIsNavigatingAway(false);
  }, []);

  const handleRetryCheckout = useCallback(() => {
    setCheckoutError(null);
    setCheckoutState("VIDEO_PREVIEW");
    setIsNavigatingAway(false);
  }, []);

  // Open URL in Safari from iOS WebView (Instagram/Facebook in-app browser)
  // Uses Safari URL schemes - works to break out of embedded WebView
  const openInSafariFromWebView = useCallback((url: string) => {
    const xSafariScheme = `x-safari-https://${url.replace(/^https?:\/\//, "")}`;
    const legacyScheme = `com-apple-mobilesafari-tab:${url}`;
    setTimeout(() => {
      window.location.href = xSafariScheme;
    }, 50);
    setTimeout(() => {
      window.location.href = legacyScheme;
    }, 400);
    setTimeout(() => {
      window.location.href = url;
    }, 1200);
  }, []);

  // Handle Instagram banner open in browser action
  const handleOpenInBrowser = useCallback(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (!url) return;

    // 1. Copy to clipboard (fallback if Safari scheme fails, or for Android)
    const copyToClipboard = () => {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(url);
      }
      return Promise.reject(new Error("Clipboard not available"));
    };
    copyToClipboard()
      .then(() => {
        setLinkCopiedForBrowser(true);
        setTimeout(() => setLinkCopiedForBrowser(false), 4000);
      })
      .catch(() => {
        /* ignore */
      });

    trackButtonClickGA(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );

    // 2. iOS: Safari URL scheme - breaks out of Instagram WebView
    if (isIOS) {
      openInSafariFromWebView(url);
      return;
    }

    // 3. Android / other: share sheet (may offer "Open in Chrome")
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(() => {});
    }
  }, [service, effectiveCountry, isIOS, openInSafariFromWebView]);

  const handleDismissInstagramBanner = useCallback(() => {
    setShowInstagramBanner(false);
    setInstagramBannerDismissed(true);

    // Track banner dismissal
    trackButtonClickGA(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
  }, [service, effectiveCountry]);

  // Safari-specific touch handler for button
  const handleCTATouchStart = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // Prevent container from handling this touch
      touchStartRef.current = null;
    },
    [],
  );

  const handleCTATouchEnd = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Container styling — reel mobile/desktop alineado con MuxVideoPlayer
  // When Instagram Explorer: always add instagram-browser-container for viewport/scroll/video fixes
  const getContainerClasses = () => {
    const baseClasses = "relative bg-black group";
    const instagramClasses = isInstagram ? "instagram-browser-container" : "";

    if (isModal) {
      return `${baseClasses} w-full h-full min-h-0 flex items-center justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (isReelMode) {
      if (isMobile) {
        // Mobile: Fixed height, no scroll, fully contained
        return `${baseClasses} w-full h-dvh max-h-dvh overflow-hidden ${instagramClasses} ${containerClassName}`;
      }
      // Desktop: Use h-full to respect parent container, no min-height to avoid scroll
      return `${baseClasses} w-full h-full flex items-start justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "contain") {
      return `${baseClasses} w-full h-full min-h-[400px] max-h-[90vh] flex items-center justify-center ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "cover") {
      if (isMobile && fullViewportOnMobile) {
        return `${baseClasses} w-full h-dvh min-h-dvh max-h-dvh overflow-hidden ${instagramClasses} ${containerClassName}`;
      }
      return `${baseClasses} w-full h-full min-h-[80vh] max-h-[90vh] ${instagramClasses} ${containerClassName}`;
    }
    return `${baseClasses} w-full h-full min-h-[400px] max-h-[800px] ${instagramClasses} ${containerClassName}`;
  };

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isReelMode || isMobile) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(".action-button-container") ||
        target.closest(".mux-cta-close-btn")
      )
        return;
      // If video is playing: click to toggle mute (same as mobile tap behavior)
      // If video is paused: click to toggle play/pause
      if (isPlaying) {
        toggleMute();
      } else {
        togglePlayPause();
      }
    },
    [isReelMode, isMobile, togglePlayPause, toggleMute, isPlaying],
  );

  const getVideoStyles = useCallback(() => {
    const id = safeCssId;
    if (isReelMode) {
      if (isMobile) {
        // Platform-specific height handling
        const heightStyles = isIOS
          ? `
            /* iOS Safari specific - use multiple fallbacks for dynamic toolbar */
            height: 100vh !important;
            height: 100dvh !important;
            height: -webkit-fill-available !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            min-height: -webkit-fill-available !important;
          `
          : `
            /* Android Chrome - dvh works well */
            height: 100dvh !important;
            height: 100vh !important;
            min-height: 100dvh !important;
            min-height: 100vh !important;
          `;

        // Landscape mode adjustments
        const landscapeStyles = isLandscape
          ? `
          /* Landscape orientation adjustments using Mux CSS variables */
          @media (orientation: landscape) {
            .mux-video-player-cta-${id} {
              height: 100dvh !important;
              height: 100vh !important;
            }
            .mux-video-player-cta-${id} mux-player {
              --media-object-fit: contain;
              --media-object-position: center;
            }
          }
        `
          : "";

        return `
          .mux-video-player-cta-${id} {
            width: 100vw !important;
            ${heightStyles}
            max-height: 100dvh !important;
            max-height: 100vh !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            overflow: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            /* GPU acceleration for smoother scrolling */
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            will-change: transform !important;
            /* TikTok style: full immersive screen */
            margin: 0 !important;
            padding: 0 !important;
          }
          .mux-video-player-cta-${id} mux-player {
            width: 100% !important;
            height: 100% !important;
            min-height: 100dvh !important;
            min-height: 100vh !important;
            max-height: 100dvh !important;
            max-height: 100vh !important;
            /* Use official Mux CSS variables for video sizing - TikTok style cover */
            --media-object-fit: cover;
            --media-object-position: center;
          }
          /* Position controls at bottom with proper spacing for CTA button using CSS parts */
          .mux-video-player-cta-${id} mux-player::part(bottom) {
            bottom: max(7rem, calc(env(safe-area-inset-bottom) + 5rem)) !important;
          }
          /* Video element inherits from --media-object-fit and --media-object-position */
          .mux-video-player-cta-${id} mux-player video {
            width: 100% !important;
            height: 100% !important;
            /* Ensure inline playback on all mobile devices */
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          /* iOS Safari specific viewport fixes */
          @supports (-webkit-touch-callout: none) {
            .mux-video-player-cta-${id} {
              height: -webkit-fill-available !important;
              min-height: -webkit-fill-available !important;
            }
          }
          /* Android specific optimizations */
          ${
            isAndroid
              ? `
          .mux-video-player-cta-${id} {
            /* Android Chrome handles dvh well */
            height: 100dvh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* Ensure inline playback on Android */
            playsinline: true !important;
          }
          `
              : ""
          }
          /* iOS specific optimizations */
          ${
            isIOS
              ? `
          .mux-video-player-cta-${id} {
            /* iOS needs -webkit-fill-available for proper height */
            height: -webkit-fill-available !important;
            /* Prevent iOS rubber-banding effect */
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* iOS requires webkit prefix */
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          `
              : ""
          }
          ${landscapeStyles}
          ${
            isInstagram
              ? `
          .mux-video-player-cta-${id} {
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-y pinch-zoom !important;
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            min-height: 100svh !important;
            height: 100svh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} button,
          .mux-video-player-cta-${id} .action-button-container button {
            -webkit-tap-highlight-color: transparent !important;
            touch-action: manipulation !important;
            min-height: 48px !important;
            min-width: 48px !important;
            -webkit-user-select: none !important;
            user-select: none !important;
          }
          `
              : ""
          }
        `;
      }
      return `
        .mux-video-player-cta-${id} {
          width: 100% !important;
          height: 100% !important;
          min-height: 100vh !important;
          display: flex !important;
          align-items: stretch !important;
          justify-content: center !important;
          background-color: #000 !important;
          position: relative !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id}[data-mux-reel-viewport="page"] {
          height: 100dvh !important;
          height: 100vh !important;
          min-height: 100dvh !important;
          min-height: 100vh !important;
        }
        .mux-video-player-cta-${id} mux-player {
          width: auto !important;
          height: 100% !important;
          max-width: min(56.25vh, 100%) !important;
          max-height: 100dvh !important;
          max-height: 100vh !important;
          min-width: 320px !important;
          margin: 0 auto !important;
          display: block !important;
          position: relative !important;
          /* Use official Mux CSS variables for video sizing */
          --media-object-fit: contain;
          --media-object-position: center;
        }
        /* Position controls at bottom with proper spacing for CTA button using CSS parts */
        .mux-video-player-cta-${id} mux-player::part(bottom) {
          bottom: max(7rem, calc(env(safe-area-inset-bottom) + 5rem)) !important;
        }
        /* Video element inherits from --media-object-fit and --media-object-position */
        .mux-video-player-cta-${id} mux-player video {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 85vw) !important;
          }
        }
        @media (min-width: 1025px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 90vw) !important;
          }
        }
        @media (min-width: 1920px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 1200px) !important;
          }
        }
      `;
    }
    if (currentDisplayMode === "cover") {
      return `
        .mux-video-player-cta-${id} mux-player {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }
    return `
      .mux-video-player-cta-${id} mux-player {
        object-fit: contain !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
      }
    `;
  }, [
    isReelMode,
    isMobile,
    isInstagram,
    isIOS,
    isAndroid,
    isLandscape,
    currentDisplayMode,
    safeCssId,
  ]);

  // Get CTA button styles to ensure visibility and proper positioning
  const getCTAButtonStyles = useCallback(() => {
    const id = safeCssId;
    const isValid = Boolean(showCTAButton);
    
    // Detectar si Special Offer está activo para aplicar estilo verde (más conversión)
    const specialOfferEnv = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER as string)?.toLowerCase().trim();
    const isSpecialOfferActive = specialOfferEnv === "true" || specialOfferEnv === "1" || specialOfferEnv === "active";

    // Base button styles (shared across all modes)
    const baseButtonStyles = `
      /* Instagram browser banner animation */
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-slide-down {
        animation: slideDown 0.3s ease-out;
      }

      .mux-video-player-cta-${id} .cta-action-button {
        z-index: 51 !important;
        position: relative !important;
        pointer-events: auto !important;
        visibility: visible !important;
        opacity: ${isValid ? 1 : 0.7} !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: ${isSpecialOfferActive 
          ? "linear-gradient(to right, #10b981, #059669, #047857) !important" 
          : "linear-gradient(to right, #a855f7, #9333ea, #ec4899) !important"};
        color: white !important;
        font-weight: 700 !important;
        letter-spacing: 0.01em !important;
        border-radius: 9999px !important;
        box-shadow: ${isSpecialOfferActive
          ? "0 4px 14px 0 rgba(16, 185, 129, 0.5), 0 2px 4px 0 rgba(5, 150, 105, 0.4) !important"
          : "0 4px 14px 0 rgba(139, 92, 246, 0.4), 0 2px 4px 0 rgba(236, 72, 153, 0.3) !important"};
        transition: all 0.3s ease-out !important;
        cursor: pointer !important;
        -webkit-tap-highlight-color: transparent !important;
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
        touch-action: manipulation !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) {
        background: ${isSpecialOfferActive
          ? "linear-gradient(to right, #059669, #047857, #065f46) !important"
          : "linear-gradient(to right, #9333ea, #7e22ce, #db2777) !important"};
        box-shadow: ${isSpecialOfferActive
          ? "0 6px 20px 0 rgba(16, 185, 129, 0.6), 0 4px 8px 0 rgba(5, 150, 105, 0.5) !important"
          : "0 6px 20px 0 rgba(139, 92, 246, 0.5), 0 4px 8px 0 rgba(236, 72, 153, 0.4) !important"};
        border-color: rgba(255, 255, 255, 0.2) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:active:not(:disabled) {
        transform: scale(0.97) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:disabled {
        background: linear-gradient(to right, #4b5563, #374151) !important;
        cursor: not-allowed !important;
        opacity: 0.7 !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        pointer-events: none !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        flex-shrink: 1 !important;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
        letter-spacing: 0.025em !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)) !important;
        transition: transform 0.3s ease-out !important;
      }
      .mux-video-player-cta-${id} .cta-action-button svg[class*="lucide-instagram"] {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) .cta-button-icon {
        transform: translate(2px, -2px) !important;
      }
    `;

    if (isReelMode && !isMobile) {
      // Desktop/tablet reel: button higher so it stays visible when close (X) or fullscreen layout compresses the view
      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 100 !important;
          position: absolute !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          /* Match mobile reel: sit on the video column bottom (column is full viewport height) */
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: 50% !important;
          right: auto !important;
          width: min(56.25vh, 90vw) !important;
          max-width: min(56.25vh, 90vw) !important;
          transform: translateX(-50%) !important;
          padding: 0 1rem !important;
          box-sizing: border-box !important;
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .mux-video-player-cta-${id} .action-button-container {
            width: min(56.25vh, 85vw) !important;
            max-width: min(56.25vh, 85vw) !important;
          }
        }
        @media (min-width: 1920px) {
          .mux-video-player-cta-${id} .action-button-container {
            width: min(56.25vh, 1200px) !important;
            max-width: min(56.25vh, 1200px) !important;
          }
        }
        .mux-video-player-cta-${id} .cta-action-button {
          min-height: 56px !important;
          height: 56px !important;
          padding: 0 1.5rem !important;
          font-size: 1rem !important;
          gap: 0.625rem !important;
          width: 100% !important;
          flex-shrink: 0 !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 1rem !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.25rem !important;
          height: 1.25rem !important;
        }
      `
      );
    }

    if (isMobile) {
      // Mobile: centered at bottom with safe-area insets (iPhone notch, home indicator)
      // Platform-specific optimizations for iOS and Android
      const platformStyles = isIOS
        ? `
        /* iOS specific - ensure proper touch handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-appearance: none !important;
          -webkit-tap-highlight-color: transparent !important;
          /* Prevent iOS text selection on long press */
          -webkit-user-select: none !important;
          -webkit-touch-callout: none !important;
        }
      `
        : isAndroid
          ? `
        /* Android specific - better ripple effect handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1) !important;
        }
      `
          : "";

      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 150 !important;
          position: fixed !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          /* TikTok Shop style - bottom with safe-area for all devices */
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: max(1rem, env(safe-area-inset-left, 1rem)) !important;
          right: max(1rem, env(safe-area-inset-right, 1rem)) !important;
          width: auto !important;
          min-width: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id} .cta-action-button {
          /* TikTok Shop style - prominent button */
          min-height: 56px !important;
          height: 56px !important;
          flex-shrink: 0 !important;
          padding: 0 1.75rem !important;
          font-size: 1.0625rem !important;
          font-weight: 800 !important;
          gap: 0.75rem !important;
          flex: 1 !important;
          max-width: 100% !important;
          width: 100% !important;
          /* Prevent double-tap zoom on button */
          touch-action: manipulation !important;
          /* Enhanced shadow for TikTok Shop look */
          box-shadow: 0 8px 24px 0 rgba(139, 92, 246, 0.6), 0 4px 12px 0 rgba(236, 72, 153, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.15) !important;
          /* Backdrop blur for better visibility on any video */
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 1rem !important;
          font-weight: 800 !important;
          letter-spacing: 0.02em !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.25rem !important;
          height: 1.25rem !important;
        }
        /* Small mobile devices (iPhone SE, small Android) */
        @media (max-width: 375px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(1rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem)) !important;
            left: max(0.75rem, env(safe-area-inset-left, 0.75rem)) !important;
            right: max(0.75rem, env(safe-area-inset-right, 0.75rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            gap: 0.625rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-icon {
            width: 1.125rem !important;
            height: 1.125rem !important;
          }
        }
        /* Extra small devices (very small Android phones) */
        @media (max-width: 320px) {
          .mux-video-player-cta-${id} .action-button-container {
            padding: 0 !important;
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(0.75rem, env(safe-area-inset-left)) !important;
            right: max(0.75rem, env(safe-area-inset-right)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
        }
        /* Medium mobile devices (iPhone 12/13/14) */
        @media (min-width: 376px) and (max-width: 414px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
        }
        /* Large mobile devices (iPhone Pro Max, large Android) */
        @media (min-width: 415px) and (max-width: 767px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 58px !important;
            height: 58px !important;
            padding: 0 1.75rem !important;
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
        }
        /* Landscape orientation - adjust for side notches, keep button visible */
        @media (orientation: landscape) and (max-height: 500px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(2rem, calc(env(safe-area-inset-left) + 0.5rem)) !important;
            right: max(2rem, calc(env(safe-area-inset-right) + 0.5rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 48px !important;
            height: 48px !important;
            padding: 0 1.25rem !important;
            font-size: 0.875rem !important;
          }
        }
        /* Instagram / Meta in-app browser: más margen inferior (chrome del WebView) */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(1.75rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
          }
        `
            : ""
        }
        ${platformStyles}
      `
      );
    }

    // Desktop non-reel mode: full width with padding, button at bottom
    return (
      baseButtonStyles +
      `
      .mux-video-player-cta-${id} .action-button-container {
        z-index: 100 !important;
        position: absolute !important;
        pointer-events: auto !important;
        visibility: visible !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        bottom: max(2rem, env(safe-area-inset-bottom)) !important;
        left: 1.5rem !important;
        right: 1.5rem !important;
        width: auto !important;
        max-width: calc(100% - 3rem) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button {
        min-height: 56px !important;
        height: 56px !important;
        padding: 0 1.5rem !important;
        font-size: 1rem !important;
        gap: 0.625rem !important;
        max-width: 32rem !important;
        width: 100% !important;
        flex-shrink: 0 !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        font-size: 1rem !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        width: 1.25rem !important;
        height: 1.25rem !important;
      }
    `
    );
  }, [
    safeCssId,
    isMobile,
    isReelMode,
    isInstagram,
    isIOS,
    isAndroid,
    showCTAButton,
  ]);

  // Function to retry loading the video
  const handleRetry = useCallback(() => {
    setHasError(false);
    setErrorMessage("");
    setIsLoading(true);
    setHasReceivedMuxMetadata(false);
    setMuxHasLoadedData(false);
    setMuxCanPlayBuffered(false);
    setHasPlaybackStarted(false);
    setShowPlayButton(false);
    setLoadingSeconds(0);
    setRetryCount(0);

    // Force reload the player
    const player = playerRef.current;
    if (player) {
      if (typeof player.load === "function") {
        player.load();
      } else if (typeof player.play === "function") {
        // Fallback: try to play
        player.play().catch((error: unknown) => {
          console.debug("[MuxVideoPlayerWithCTA] Retry play failed:", error);
        });
      }
    }
  }, []);

  // Get optimized thumbnail URL for fallback display and faster loading
  // Only generate thumbnail URL if playbackId is valid and not empty
  // Use Mux Image API with optimizations for faster load and better UX
  // Mobile-first optimization: Use appropriate sizes for device viewport
  const getThumbnailWidth = () => {
    if (typeof window === "undefined") return "1080";
    // Use actual viewport width for better responsive loading
    const viewportWidth = window.innerWidth;
    const dpr = window.devicePixelRatio || 1;

    // Calculate optimal width based on viewport and pixel density
    // Cap at 1920 for performance (max needed for most screens)
    const optimalWidth = Math.min(viewportWidth * dpr, 1920);

    // Round to nearest standard width for better CDN caching
    if (optimalWidth <= 480) return "480";
    if (optimalWidth <= 720) return "720";
    if (optimalWidth <= 1080) return "1080";
    return "1920";
  };

  const thumbnailFitMode = isReelMode && isMobile ? "smartcrop" : "preserve";
  const thumbnailObjectFit = isReelMode && isMobile ? "cover" : "contain";

  const thumbnailUrl =
    effectivePlaybackId && effectivePlaybackId.trim() !== ""
      ? `https://image.mux.com/${effectivePlaybackId}/thumbnail.webp?time=0&width=${getThumbnailWidth()}&fit_mode=${thumbnailFitMode}&quality=${
          isMobile ? "75" : "80"
        }`
      : null;

  // Generate low-quality placeholder for instant display (LQIP - Low Quality Image Placeholder)
  // Ultra-fast load for perceived performance, especially on slow mobile connections
  const placeholderUrl =
    effectivePlaybackId && effectivePlaybackId.trim() !== ""
      ? `https://image.mux.com/${effectivePlaybackId}/thumbnail.webp?time=0&width=64&fit_mode=${thumbnailFitMode}&quality=50`
      : null;

  /**
   * Único `<MuxPlayer>` del componente — lo consumen tanto la rama `variant="modal"`
   * como la inline. Antes eran dos bloques JSX duplicados (~130 líneas c/u) que
   * divergieron: el inline —el del embudo DualCTA, o sea el camino de conversión—
   * había perdido `lang`, así que el player quedaba sin localizar justo ahí
   * (confirmado en replay PostHog 019f86db: `<mux-player>` sin atributo `lang`).
   */
  const renderMuxPlayer = () => (
    <MuxPlayer
      key={muxPlayerKey}
      ref={playerRef}
      streamType="on-demand"
      playbackId={effectivePlaybackId}
      accentColor="#ac39f2"
      // TikTok Shop style: NO controls in any country - immersive experience (via CSS)
      // Intelligent autoplay: try with sound first, fallback to muted (TikTok/Reels style)
      // Instagram Explorer often blocks autoplay with sound, so use "any" for fallback
      // Can be disabled with autoPlay={false} for manual play only
      lang={detectedLanguage}
      autoPlay={autoPlay}
      loop={loop}
      muted={muxPlayerMuted}
      playsInline
      // Optimize preload strategy for mobile networks:
      // - "metadata": Fast initial load, user can start watching quickly
      // - "auto": Desktop with good connection can buffer ahead
      preload={
        isMobile || isInstagram || muxArcPlaybackTune ? "metadata" : "auto"
      }
      // Disable keyboard shortcuts (not needed for mobile/touch)
      nohotkeys
      // Progressive thumbnail loading for instant visual feedback.
      // parentOwnsPreview: poster="" (string vacío) DESACTIVA el poster
      // interno — con undefined mux-player fetchea su default full-size
      // (thumbnail.webp sin width, ~100KB al pedo en mobile; replay 019f6c0a…).
      poster={parentOwnsPreview ? "" : thumbnailUrl || undefined}
      placeholder={parentOwnsPreview ? undefined : placeholderUrl || undefined}
      // iOS-specific: Enable inline playback without fullscreen
      {...(isIOS
        ? { "webkit-playsinline": "true", "x5-playsinline": "true" }
        : {})}
      onPlay={handlePlay}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onEnded={handleEnded}
      onError={handleMuxPlayerError}
      onLoadedMetadata={handleLoadedMetadata}
      onLoadedData={handleMuxLoadedData}
      onCanPlay={handleCanPlay}
      onTimeUpdate={handleTimeUpdateForTelemetry}
      onStalled={() => {
        logVideoEvent({
          event: "video_stalled",
          country: effectiveCountry,
          service,
          playbackId: effectivePlaybackId,
        });
      }}
      onWaiting={() => {
        logVideoEvent({
          event: "video_buffering",
          country: effectiveCountry,
          service,
          playbackId: effectivePlaybackId,
        });
      }}
      style={
        {
          width: "100%",
          height: "100%",
          // Use official Mux CSS variables for video object fit
          "--media-object-fit": isReelMode && isMobile ? "cover" : "contain",
          "--media-object-position": "center",
          // Mobile GPU acceleration for smoother playback
          ...(isMobile
            ? {
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                WebkitFontSmoothing: "antialiased",
              }
            : {}),
        } as React.CSSProperties & Record<`--${string}`, string>
      }
      {...(MUX_DATA_ENV_KEY ? { envKey: MUX_DATA_ENV_KEY } : {})}
      metadata={{
        video_id: effectivePlaybackId,
        video_title: muxDataVideoTitle || title || "",
        video_stream_type: "on-demand",
        ...(muxDataVideoSeries ? { video_series: muxDataVideoSeries } : {}),
        ...(slotKey
          ? {
              video_ab_slot_key: slotKey,
              video_ab_variant_label: slotVariantLabel || "",
            }
          : {}),
      }}
      // Performance optimizations for Mux
      startTime={0}
      primaryColor="#ac39f2"
      secondaryColor="#ec4899"
      // Adaptive resolution based on device and connection:
      // Mobile: 720p is optimal balance (quality vs data usage)
      // Desktop: 1080p for better quality on larger screens
      maxResolution={
        isInstagram ? "480p" : isMobile || muxArcPlaybackTune ? "720p" : "1080p"
      }
      // https://docs.mux.com/guides/player-advanced-usage#change-playback-engine
      // iOS IG: native. Android IG: MSE+hls.js (native demuxer often
      // hits DEMUXER_ERROR_COULD_NOT_PARSE ~20s into High-profile HLS).
      {...(isInstagram && isIOS
        ? { preferPlayback: "native" as const }
        : isInstagram && isAndroid
          ? { preferPlayback: "mse" as const }
          : muxArcPlaybackTune
            ? { preferPlayback: "mse" as const }
            : {})}
      // Thumbnail optimization: Show frame at 0s for instant preview
      thumbnailTime={0}
      minPreloadSegments={
        isMobile || isInstagram || muxArcPlaybackTune ? 2 : 1
      }
      capRenditionToPlayerSize={Boolean(
        isMobile || isInstagram || muxArcPlaybackTune,
      )}
      // Audio optimizations
      defaultShowRemainingTime={false}
      {...(fragileMseHlsConfig ? { _hlsConfig: fragileMseHlsConfig } : {})}
    />
  );

  const renderMuxLoadChrome = () => (
    <>
      {showHeavyVideoLoader && (
        <div
          className={`absolute inset-0 z-40 flex items-center justify-center transition-colors duration-500 ${
            muxHasLoadedData
              ? "bg-black/25 backdrop-blur-[1px]"
              : "bg-black/42 backdrop-blur-[2px]"
          }`}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("loadingVideo", detectedLanguage)}
          style={{
            paddingTop: "max(1rem, env(safe-area-inset-top, 0px))",
            paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
            paddingLeft: "max(1rem, env(safe-area-inset-left, 0px))",
            paddingRight: "max(1rem, env(safe-area-inset-right, 0px))",
          }}
        >
          <div className="text-center space-y-4 px-6 py-4 max-w-md mx-auto">
            <div className="flex justify-center gap-2" aria-hidden="true">
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  hasReceivedMuxMetadata
                    ? "bg-fuchsia-500"
                    : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxHasLoadedData ? "bg-fuchsia-500" : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxCanPlayBuffered
                    ? "bg-fuchsia-400"
                    : hasReceivedMuxMetadata && muxHasLoadedData
                      ? "bg-white/40 animate-pulse"
                      : "bg-white/15"
                }`}
              />
            </div>
            <div className="relative mx-auto w-16 h-16 md:w-20 md:h-20">
              <div
                className="absolute inset-0 w-full h-full border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"
                style={{
                  animationDuration: "1s",
                  willChange: "transform",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-6 h-6 md:w-8 md:h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div
                className="absolute inset-0 w-full h-full bg-purple-500 rounded-full blur-lg opacity-20"
                style={{ willChange: "opacity" }}
              />
            </div>

            <div className="space-y-1">
              <p className="text-white text-sm md:text-base font-semibold tracking-wide">
                {!hasReceivedMuxMetadata
                  ? t("loadingVideo", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("loadingVideo", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
              </p>
              <p className="text-white/85 text-xs font-medium">
                {!hasReceivedMuxMetadata
                  ? t("connectingToPlayer", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("decodingPreview", detectedLanguage)
                    : detectedLanguage === "es"
                      ? `Tiempo de carga: ${loadingSeconds}s`
                      : `Loading time: ${loadingSeconds}s`}
              </p>
              {!isMobile && hasReceivedMuxMetadata && muxHasLoadedData && (
                <p className="text-gray-300 text-xs font-medium opacity-85">
                  {t("startingPlayback", detectedLanguage)}
                </p>
              )}
              {shouldShowSlowConnectionHint && (
                <p className="text-purple-200 text-xs font-medium">
                  {isLikelySlowNetwork
                    ? t("slowConnection", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
                </p>
              )}
              {isInstagram && (
                <p className="text-white/70 text-xs leading-relaxed pt-1">
                  {t("inAppBrowserVideoLoadHint", detectedLanguage)}
                </p>
              )}
            </div>

            {isMobile && (hasReceivedMuxMetadata || muxHasLoadedData) && (
              <div className="flex items-center justify-center gap-1.5">
                <div
                  className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "0ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "150ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "300ms",
                    animationDuration: "1s",
                  }}
                />
              </div>
            )}
            {loadingSeconds >= 10 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetry();
                }}
                className="mt-3 px-4 py-2 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              >
                {t("retryLoading", detectedLanguage)}
              </button>
            )}
          </div>
        </div>
      )}
      {showLightVideoLoader && (
        <div
          className={`absolute inset-x-0 z-40 flex justify-center px-4 pointer-events-none ${
            isInstagram ? "bottom-20 md:bottom-24" : "bottom-6 md:bottom-8"
          }`}
          style={{
            paddingBottom: isInstagram
              ? "max(2rem, calc(env(safe-area-inset-bottom, 0px) + 3.5rem))"
              : "max(0.5rem, env(safe-area-inset-bottom, 0px))",
          }}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("playbackReadyBrief", detectedLanguage)}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/82 px-4 py-2.5 shadow-lg backdrop-blur-md max-w-[min(100%,22rem)]">
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-fuchsia-300"
              aria-hidden
            />
            <span className="text-xs font-medium text-white/95 text-center">
              {t("playbackReadyBrief", detectedLanguage)}
            </span>
          </div>
        </div>
      )}
    </>
  );

  // No playbackId — esperar hidratación runtime antes de mostrar error duro (CF-native).
  if (!effectivePlaybackId || effectivePlaybackId.trim() === "") {
    if (!brandRuntimeHydrated) {
      return (
        <div
          className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
        >
          <div className="text-center space-y-4">
            <Loader2 className="w-8 h-8 text-white animate-spin mx-auto" />
            <div className="text-white text-sm">
              {t("playbackReadyBrief", detectedLanguage)}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <span className="text-yellow-400 text-2xl">⚙️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">
            Video Configuration Missing
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            No video playback ID configured for this service.
            {service && ` (Service: ${service})`}
            {countryCode && ` (Country: ${countryCode})`}
          </p>
          <p className="text-xs text-gray-400">
            Please check environment variables or service configuration.
          </p>
        </div>
      </div>
    );
  }

  // Error state - NUNCA bloquear para channel_invite (telegram groups). Siempre mostrar video + CTA.
  const isJoinFlow = ["telegram", "telegram_groups", "channel_invite"].includes(
    service ?? "",
  );
  if (
    !isJoinFlow &&
    hasError &&
    errorMessage &&
    !errorMessage.toLowerCase().includes("network") &&
    !errorMessage.toLowerCase().includes("connection")
  ) {
    // Only block for invalid videos, not network errors
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
        style={
          thumbnailUrl && !thumbnailError
            ? {
                backgroundImage: `url(${thumbnailUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">⚠️</span>
          </div>
          {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
          <p className="text-sm text-gray-300 mb-4">{errorMessage}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setHasError(false);
                setErrorMessage("");
                onClose?.();
              }}
              className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700/70 text-white rounded-full text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>

        {/* Hidden image to preload thumbnail and detect errors */}
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            className="hidden"
            onError={() => {
              setThumbnailError(true);
            }}
            onLoad={() => {
              setThumbnailError(false);
            }}
            loading="lazy"
          />
        )}
      </div>
    );
  }

  // For network errors, continue rendering - show video player with poster and CTA
  // This ensures BioTemplate always provides a good UX even with VPN/network issues

  // Show loading state while service config is being loaded
  // Note: For services, apiVideoConfig always provides valid fallbacks, so we don't need to wait
  // This only blocks rendering when we truly have no playbackId available
  if (isLoadingConfig && !playbackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto" />
          <div className="text-white text-sm">
            Loading video configuration...
          </div>
        </div>
      </div>
    );
  }

  if (!effectivePlaybackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-white text-sm">
          {service
            ? `No playback ID configured for service: ${service}`
            : "No playback ID provided"}
        </div>
      </div>
    );
  }

  // Get pack info (effectivePackId: channel_invite or overridePackId e.g. exclusive); fallback to static so checkout always renders when we have clientSecret.
  // Para sorpresa/exclusive priorizar siempre static para que el custom element tenga precio y se renderice.
  const packPricing =
    (dashboardPlanFallback
      ? {
          id: effectivePackId,
          name: dashboardPlanFallback.productName,
          nameEs: dashboardPlanFallback.productName,
          description: "",
          descriptionEs: "",
          amount: dashboardPlanFallback.amountCents,
          currency: dashboardPlanFallback.currency,
        }
      : null) ??
    (overridePackId
      ? getPackPricingStatic(effectivePackId, effectiveCountry)
      : null) ??
    getPackPricing(effectivePackId, effectiveCountry) ??
    getPackPricingStatic(effectivePackId, effectiveCountry);

  // Precio mostrado y enviado al PI: pack list + reglas exclusivo + bonus "suerte" (solo Stripe).
  const watcherCheckoutPricing = computeStripePackCheckoutAmounts({
    baseAmountCents: packPricing?.amount ?? 0,
    packId: effectivePackId,
    watcherLuckPercent: stripeWatcherLuckPercent,
  });

  const checkoutDisplayAmount =
    packPricing == null ? 0 : watcherCheckoutPricing.finalAmountCents;
  const checkoutOriginalAmount =
    watcherCheckoutPricing.showDiscountBadge
      ? watcherCheckoutPricing.originalListAmountCents
      : undefined;
  const checkoutDiscountPercent =
    watcherCheckoutPricing.discountPercentDisplay ?? undefined;

  // Format price for display
  const formatPrice = (amount: number, currency: string) =>
    formatAmountFromCents(amount, currency);

  // Email collection removed - Stripe collects email in billing details

  // Loading state while creating payment intent
  const checkoutContentWrapper = (content: React.ReactNode) => {
    const inner = (
      <div
        className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
        style={{ minHeight: 0, flex: 1 }}
      >
        {content}
      </div>
    );
    if (isModal) {
      return (
        <div
          role="dialog"
          aria-modal="true"
          className={`w-full h-full flex items-center justify-center p-4 bg-black/70 ${isInstagram ? "instagram-browser-modal" : ""}`}
        >
          <div className="w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col">
            {inner}
          </div>
        </div>
      );
    }
    return inner;
  };

  if (ctaBusy) {
    const loadingCopy = isNavigatingAway
      ? t("openingCheckoutPage", detectedLanguage)
      : t("preparingCheckout", detectedLanguage);
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex flex-col items-center justify-center bg-black gap-4 p-4"
        aria-busy="true"
        aria-live="polite"
        aria-label={loadingCopy}
      >
        <div
          className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-white/80 text-sm text-center max-w-xs px-2">
          {loadingCopy}
        </p>
      </div>,
    );
  }

  // Render checkout state: solo si tenemos clientSecret, packPricing y monto final válido (coincide con PaymentIntent)
  if (checkoutState === "CHECKOUT" && clientSecret && packPricing && checkoutDisplayAmount > 0) {
    const stripePromise = getStripe();
    if (!stripePromise) {
      if (!muxStripeJsUnavailableTrackedRef.current) {
        muxStripeJsUnavailableTrackedRef.current = true;
        trackStripePayFailurePostHog({
          stage: "stripe_js_unavailable",
          flow_surface: "mux_video_cta",
          failure_kind: "stripe_promise_null",
          pack_id: effectivePackId,
        });
      }
      return checkoutContentWrapper(
        <div className="w-full h-full flex items-center justify-center bg-black p-4">
          <div className="text-white text-center">
            <p className="text-red-400 mb-4">Checkout not configured</p>
            <button
              onClick={handleBackToVideo}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              Volver
            </button>
          </div>
        </div>,
      );
    }

    return checkoutContentWrapper(
      <div className="w-full h-full bg-black flex flex-col min-h-0">
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: STRIPE_APPEARANCE,
            locale: detectedLanguage === "es" ? "es" : "en",
            loader: 'auto',
          }}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <StripeCheckoutForm
              amount={checkoutDisplayAmount}
              currency="usd"
              packName={
                detectedLanguage === "es" ? packPricing.nameEs : packPricing.name
              }
              packThumbnail={packPricing.thumbnail}
              originalAmount={checkoutOriginalAmount}
              discountPercent={checkoutDiscountPercent}
              language={detectedLanguage === "es" ? "es" : "en"}
              onSuccess={handleCheckoutSuccess}
              onError={handleCheckoutError}
              onBack={handleBackToVideo}
              flowSurface="mux_video_stripe_checkout"
            />
          </div>
        </Elements>
      </div>,
    );
  }

  // Note: SUCCESS state redirects to /thank-you or /gracias, no need to render

  // Render invalid playbackId error (before attempting to load video)
  if (!isValidPlaybackId && !isLoadingConfig) {
    const errorTitle =
      detectedLanguage === "es" ? "Video no disponible" : "Video unavailable";
    const errorMsg =
      detectedLanguage === "es"
        ? "El video no está configurado correctamente. Por favor contacta a soporte."
        : "The video is not configured correctly. Please contact support.";
    const backLabel = detectedLanguage === "es" ? "Cerrar" : "Close";

    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">{errorMsg}</p>
            {import.meta.env.DEV && (
              <p className="text-white/50 text-xs mt-4 font-mono">
                Debug: playbackId="{effectivePlaybackId}" country=
                {effectiveCountry} service={service}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation"
            >
              {backLabel}
            </button>
          )}
        </div>
      </div>,
    );
  }

  // Render error state (same container as video for responsive)
  if (checkoutState === "ERROR") {
    const errorTitle = t("paymentError", detectedLanguage);
    const errorDefaultMsg = t("paymentErrorMsg", detectedLanguage);
    const retryLabel = t("retry", detectedLanguage);
    const backLabel = t("back", detectedLanguage);
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">
              {checkoutError || errorDefaultMsg}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleRetryCheckout}
              className="w-full bg-white text-black font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/90 active:bg-white/95 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {retryLabel}
            </button>
            <button
              type="button"
              onClick={handleBackToVideo}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>,
    );
  }

  // If content is inactive for this country (API soft delete), show message
  if (!isContentActive) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8 text-center">
        <div className="max-w-md">
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Contenido no disponible
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Este contenido no está disponible en tu región en este momento.
          </p>
        </div>
      </div>
    );
  }

  // Render video preview (default state)
  return (
    <>
      {/* Custom styles for video player */}
      <style>{getVideoStyles()}</style>
      {/* Custom styles for CTA button to ensure visibility and perfect responsiveness */}
      <style>{getCTAButtonStyles()}</style>
      {/* Mux Player custom styling using official CSS variables and parts */}
      <style>{`
        /* Use Mux official CSS variable to control media object fit */
        .mux-video-player-cta-${safeCssId} mux-player {
          --media-object-fit: ${isReelMode && isMobile ? "cover" : "contain"};
          --media-object-position: center;
        }
        ${
          parentOwnsPreview
            ? `
        .mux-video-player-cta-${safeCssId}.mux-defer-player-visible mux-player {
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        .mux-video-player-cta-${safeCssId}:not(.mux-defer-player-visible) mux-player {
          opacity: 1 !important;
          visibility: visible !important;
          transition: opacity 0.45s ease-out;
        }
        `
            : ""
        }
        
        /* Instagram Explorer optimizations */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${safeCssId} mux-player {
            /* Force native playback for better Instagram compatibility */
            -webkit-playsinline: true;
            playsinline: true;
            /* Prevent Instagram from hijacking video controls */
            pointer-events: auto;
          }
          .mux-video-player-cta-${safeCssId} mux-player video {
            /* Ensure video plays inline in Instagram without fullscreen */
            -webkit-playsinline: true;
            playsinline: true;
            /* Prevent Instagram scroll interference */
            touch-action: none;
          }
        `
            : ""
        }
        
        /* TikTok Shop style: Hide ALL controls for immersive experience in all countries */
        .mux-video-player-cta-${safeCssId} mux-player {
          /* Hide center play button for seamless autoplay experience */
          --center-play-button: none;
          /* Hide time displays for cleaner UI */
          --time-display: none;
          --duration-display: none;
          /* Hide all control buttons */
          --bottom-play-button: none;
          --seek-backward-button: none;
          --seek-forward-button: none;
          --mute-button: none;
          --captions-button: none;
          --airplay-button: none;
          --pip-button: none;
          --fullscreen-button: none;
          --cast-button: none;
          --playback-rate-button: none;
          --volume-range: none;
          --time-range: none;
          /* Hide control bar completely */
          --controls: none;
          /* Hide Mux native "Unmute" chrome + error dialog ("Error NaN" on duration race) */
          --dialog: none;
        }
        .mux-video-player-cta-${safeCssId} mux-player::part(center play button),
        .mux-video-player-cta-${safeCssId} mux-player::part(dialog),
        .mux-video-player-cta-${safeCssId} mux-player media-error-dialog,
        .mux-video-player-cta-${safeCssId} mux-player .autoplay-unmute,
        .mux-video-player-cta-${safeCssId} mux-player [class*="autoplay-unmute"] {
          display: none !important;
          pointer-events: none !important;
        }
        
        /* Floating CTA (buy) must sit above video controls */
        .mux-video-player-cta-${safeCssId} .action-button-container {
          z-index: 100 !important;
        }

        /* Full-frame loading ambience (conversion: no "empty black" flashes) */
        @keyframes muxLoadShimmer-${safeCssId} {
          0% {
            transform: translateX(-55%);
            opacity: 0.2;
          }
          45% {
            opacity: 0.55;
          }
          100% {
            transform: translateX(55%);
            opacity: 0.2;
          }
        }
        .mux-video-player-cta-${safeCssId} .mux-load-shimmer-${safeCssId} {
          position: absolute;
          inset: 0;
          width: 220%;
          left: -60%;
          background: linear-gradient(
            104deg,
            transparent 42%,
            rgba(236, 72, 153, 0.12) 50%,
            rgba(167, 139, 250, 0.2) 52%,
            transparent 62%
          );
          animation: muxLoadShimmer-${safeCssId} 3s ease-in-out infinite;
          mix-blend-mode: screen;
          pointer-events: none;
        }
      `}</style>
      {isModal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || "Video"}
          className={`w-full h-full flex items-center justify-center p-4 bg-black/70 transition-opacity duration-200 ${
            modalMounted ? "opacity-100" : "opacity-0"
          } ${isInstagram ? "instagram-browser-modal" : ""}`}
          onClick={handleBackdropClick}
        >
          <div
            className={`w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col ui-transition duration-200 ${
              modalMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}${
                deferMuxPlayerVisible ? " mux-defer-player-visible" : ""
              }`}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onClick={handleContainerClick}
              role="presentation"
              aria-busy={awaitingFirstPlayback}
              style={
                {
                  // Transparent durante loading cuando el padre provee poster →
                  // evita flash de fondo negro sobre el poster del DualCTA.
                  backgroundColor: hideLoadingShimmer && awaitingFirstPlayback
                    ? "transparent"
                    : "#000000",
                  position: "relative",
                  minHeight: 0,
                  flex: 1,
                } as React.CSSProperties
              }
            >
              {/* Video container with relative positioning for button overlay */}
              <div
                className={
                  isReelMode && !isMobile
                    ? "w-full h-full min-h-0 flex items-stretch justify-center relative"
                    : "w-full h-full flex items-center justify-center relative"
                }
                style={{ position: "relative", width: "100%", height: "100%" }}
              >
                {awaitingFirstPlayback && !hideLoadingShimmer && (
                  <div
                    className="absolute inset-0 z-[22] overflow-hidden pointer-events-none"
                    aria-hidden
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-purple-950/55" />
                    <div className={`mux-load-shimmer-${safeCssId}`} />
                  </div>
                )}
                {renderMuxPlayer()}
              </div>

              {/* Descuento suerte: muestra después de tiempo de reproducción (no molesta el inicio) */}
              {showWatcherLuckBanner &&
                stripeWatcherLuckEligible &&
                stripeWatcherLuckPercent > 0 &&
                !showHeavyVideoLoader && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                    style={{
                      bottom:
                        "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                    }}
                  >
                    <div
                      className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-2 rounded-lg border border-white/10 bg-black/38 px-3 py-2 shadow-md backdrop-blur-sm"
                      role="status"
                      aria-live="polite"
                    >
                      <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                        <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                        {" "}
                        {detectedLanguage === "es"
                          ? "extra en membresía (Stripe)."
                          : "extra on membership (Stripe)."}
                      </p>
                      <button
                        type="button"
                        aria-label={
                          detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                        }
                        className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/55 motion-reduce:transition-none"
                        onClick={dismissWatcherLuckBanner}
                      >
                        <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                      </button>
                    </div>
                  </div>
                )}

              {/* Instagram browser suggestion banner (dismissible, non-intrusive) */}
              {showInstagramBanner && isInstagram && isMobile && !showHeavyVideoLoader && (
                <div
                  className="absolute top-0 left-0 right-0 z-50 px-3"
                  style={{ marginTop: "max(0.5rem, env(safe-area-inset-top, 6px))" }}
                >
                  <div className="bg-black/40 backdrop-blur-sm rounded-full px-3.5 py-2 flex items-center gap-2 border border-white/[0.06]">
                    <ExternalLink className="w-3.5 h-3.5 text-white/50 shrink-0" strokeWidth={1.5} />
                    {linkCopiedForBrowser ? (
                      <>
                        <span className="text-white/70 text-[11px] flex-1 leading-tight truncate">
                          {t("pasteInBrowser", detectedLanguage)}
                        </span>
                        {isIOS && (
                          <a
                            href={typeof window !== "undefined" ? window.location.href : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/60 text-[11px] underline underline-offset-2 shrink-0"
                          >
                            {t("longPressHint", detectedLanguage)}
                          </a>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="text-white/65 text-[11px] flex-1 leading-tight truncate">
                          {t("betterExperience", detectedLanguage)}
                        </span>
                        <button
                          type="button"
                          onClick={handleOpenInBrowser}
                          className="text-white/80 text-[11px] font-medium shrink-0 underline underline-offset-2 active:text-white transition-colors"
                        >
                          {t("openInBrowser", detectedLanguage)}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={handleDismissInstagramBanner}
                      className="text-white/35 hover:text-white/65 shrink-0 transition-colors"
                      aria-label="Dismiss"
                    >
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              )}

              {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
              {showCTAButton && (
                <div
                  className={`action-button-container ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    zIndex: 100,
                    // Garantiza visibilidad encima del loading overlay (z-40) y thumbnail (z-30)
                    isolation: "isolate",
                  }}
                >
                  <div className="flex flex-col items-center gap-2 mb-2">
                    {muxFriendlyBanner && (
                      <p
                        className="text-white/45 text-[10px] sm:text-[11px] text-center px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                        role="note"
                      >
                        {muxFriendlyBanner}
                      </p>
                    )}
                    {ctaSubtitle && (
                      <p className="cta-subtitle text-white/90 text-sm md:text-base text-center drop-shadow-md">
                        {ctaSubtitle}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCTAClick}
                    onTouchStart={handleCTATouchStart}
                    onTouchEnd={handleCTATouchEnd}
                    disabled={!showCTAButton || ctaBusy}
                    className="cta-action-button"
                    aria-label={ctaLabel}
                    aria-disabled={!showCTAButton || ctaBusy}
                    // touch-action: manipulation elimina el delay de 300ms en Android/iOS
                    // y desactiva gestos no deseados (zoom, scroll) en el botón
                    style={{ touchAction: "manipulation" }}
                  >
                    {ctaBusy ? (
                      <Loader2
                        className={`${
                          isMobile ? "w-4 h-4" : "w-5 h-5"
                        } shrink-0 animate-spin pointer-events-none`}
                        aria-hidden
                      />
                    ) : (
                      showInstagramIcon && (
                        <Instagram
                          className={`${
                            isMobile ? "w-4 h-4" : "w-5 h-5"
                          } shrink-0 pointer-events-none`}
                          strokeWidth={2.5}
                          style={{
                            filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                          }}
                        />
                      )
                    )}
                    <span className="cta-button-text">{ctaLabel}</span>
                    {!ctaBusy && (
                      <ExternalLink
                        className="cta-button-icon action-button-icon"
                        strokeWidth={2.5}
                      />
                    )}
                  </button>
                  {/* AR / GeoSim AR: logo MP bajo el CTA de conversión */}
                  {showCTAButton &&
                    isArgentinaPaymentContext(effectiveCountry, effectiveCountry) && (
                      <div
                        className="mt-2 flex justify-center rounded-lg bg-white/95 px-3 py-1.5 shadow-sm"
                        aria-label="Pago seguro con Mercado Pago"
                      >
                        <img
                          src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                          alt="Mercado Pago — pago seguro"
                          className="h-6 w-auto max-w-[min(100%,180px)] object-contain md:h-7"
                          loading="lazy"
                          draggable={false}
                        />
                      </div>
                    )}
                </div>
              )}

              {/* Progressive image loading: LQIP (Low Quality Image Placeholder) → Full thumbnail */}
              {/* Shows instantly while video buffers, crucial for mobile UX */}
              {awaitingFirstPlayback &&
                !parentOwnsPreview &&
                (placeholderUrl || thumbnailUrl) &&
                !thumbnailError && (
                  <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                    {/* LQIP - Blur-up effect for instant perceived load */}
                    {placeholderUrl && (
                      <div
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                          backgroundImage: `url(${placeholderUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          filter: "blur(20px)",
                          transform: "scale(1.1)", // Prevent blur edges
                          opacity: thumbnailUrl ? 0.5 : 1,
                        }}
                      />
                    )}
                    {/* Full quality thumbnail - fades in when loaded */}
                    {thumbnailUrl && (
                      <div
                        className="absolute inset-0 animate-fade-in"
                        style={{
                          backgroundImage: `url(${thumbnailUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          animationDuration: "0.5s",
                          animationFillMode: "both",
                        }}
                      />
                    )}
                    {/* Dark overlay for better spinner visibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
                  </div>
                )}

              {renderMuxLoadChrome()}

              {/* Unmute affordance — center + high contrast (TikTok-style).
                  Was a tiny top pill at z-30, covered by IG banner (z-50) in
                  Meta IAB → users watched muted and bounced (session 019f43b4). */}
              {showMuteIndicator && isPlaying && !hideUnmutePrompt && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMute();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMute();
                  }}
                  className="absolute left-1/2 top-[42%] z-[60] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2.5 cursor-pointer touch-manipulation transition-opacity duration-300"
                  style={{
                    opacity: showMuteIndicator ? 1 : 0,
                    WebkitTapHighlightColor: "transparent",
                    minWidth: 96,
                    minHeight: 96,
                  }}
                  aria-label={t("enableAudio", detectedLanguage)}
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/35 bg-black/55 text-white shadow-[0_8px_28px_rgba(0,0,0,0.45)] backdrop-blur-md animate-pulse ring-2 ring-white/15">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.25}
                        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                      />
                      <line x1="18" y1="6" x2="6" y2="18" strokeWidth={2.25} />
                    </svg>
                  </span>
                  <span className="rounded-full border border-white/25 bg-black/60 px-3.5 py-1.5 text-[13px] font-semibold tracking-tight text-white shadow-md backdrop-blur-md">
                    {t("tapForSound", detectedLanguage)}
                  </span>
                </button>
              )}

              {/* Play button overlay - shown if autoplay fails (sin texto de audio si hideUnmutePrompt) */}
              {!isPlaying && showPlayButton && (
                <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 hover:bg-white active:bg-white/90 flex items-center justify-center shadow-xl ui-transition duration-200 hover:scale-105 active:scale-95 touch-manipulation select-none border border-black/10"
                    style={{
                      WebkitTapHighlightColor: "transparent",
                      WebkitTouchCallout: "none",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                      cursor: "pointer",
                      minWidth: "96px",
                      minHeight: "96px",
                    }}
                    aria-label={t("tapToPlayWithSound", detectedLanguage)}
                  >
                    <Play
                      className="w-10 h-10 md:w-12 md:h-12 text-black ml-1"
                      fill="currentColor"
                      strokeWidth={2}
                    />
                  </button>
                  {!hideUnmutePrompt && (
                    <div className="bg-transparent backdrop-blur-md text-white/85 px-3 py-1.5 rounded-full text-[11px] font-medium flex items-center gap-1.5 border border-white/20">
                      <svg
                        className="w-3.5 h-3.5 opacity-90"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                        />
                      </svg>
                      <span className="tracking-tight">
                        {t("tapToPlayWithSound", detectedLanguage)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {onClose && (
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className={`mux-cta-close-btn fixed z-[200] min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 active:bg-black/90 text-white flex items-center justify-center ui-transition duration-200 touch-manipulation outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  style={{
                    top: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-top, 0.75rem))`,
                    right: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-right, 0.75rem))`,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  }}
                  aria-label="Cerrar"
                >
                  <X className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}${
            deferMuxPlayerVisible ? " mux-defer-player-visible" : ""
          }`}
          data-mux-reel-viewport={isReelMode && !isMobile ? "page" : undefined}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={handleContainerClick}
          role="presentation"
          aria-busy={awaitingFirstPlayback}
          style={
            {
              backgroundColor: hideLoadingShimmer && awaitingFirstPlayback
                ? "transparent"
                : "#000000",
              position: isReelMode && isMobile ? "fixed" : "relative",
              ...(isMobile
                ? {
                    // TikTok style: full screen immersive
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "100dvh",
                    maxHeight: "100dvh",
                    minHeight: "100vh",
                    overflow: "hidden",
                    width: "100vw",
                  }
                : {
                    minHeight: "100dvh",
                    ...(isReelMode
                      ? {
                          height: "100dvh",
                          minHeight: "100dvh",
                        }
                      : {}),
                  }),
              ...(isInstagram
                ? {
                    touchAction: "pan-y pinch-zoom",
                    WebkitTransform: "translateZ(0)",
                    transform: "translateZ(0)",
                  }
                : {}),
            } as React.CSSProperties
          }
        >
          {/* Video container: on mobile reel use absolute inset-0 so video fills entire screen */}
          <div
            className={
              isReelMode && !isMobile
                ? "w-full h-full min-h-[100dvh] flex items-stretch justify-center relative"
                : isReelMode && isMobile
                  ? "absolute inset-0"
                  : "w-full h-full flex items-center justify-center relative"
            }
            style={
              isReelMode && isMobile
                ? {
                    position: "absolute" as const,
                    inset: 0,
                    width: "100%",
                    height: "100%",
                  }
                : isReelMode && !isMobile
                  ? {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                      minHeight: "100dvh",
                    }
                  : {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                    }
            }
          >
            {awaitingFirstPlayback && !hideLoadingShimmer && (
              <div
                className="absolute inset-0 z-[22] overflow-hidden pointer-events-none"
                aria-hidden
              >
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-purple-950/55" />
                <div className={`mux-load-shimmer-${safeCssId}`} />
              </div>
            )}
            {renderMuxPlayer()}
          </div>

          {showWatcherLuckBanner &&
            stripeWatcherLuckEligible &&
            stripeWatcherLuckPercent > 0 &&
            !showHeavyVideoLoader && (
              <div
                className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                style={{
                  bottom:
                    "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                }}
              >
                <div
                  className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-1.5 rounded-lg border border-white/10 bg-black/38 px-2.5 py-1.5 shadow-md backdrop-blur-sm"
                  role="status"
                  aria-live="polite"
                >
                  <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                    <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                    {" "}
                    {detectedLanguage === "es"
                      ? "extra en membresía (Stripe)."
                      : "extra on membership (Stripe)."}
                  </p>
                  <button
                    type="button"
                    aria-label={
                      detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                    }
                    className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/55 motion-reduce:transition-none"
                    onClick={dismissWatcherLuckBanner}
                  >
                    <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                  </button>
                </div>
              </div>
            )}

          {/* Instagram browser suggestion banner (dismissible, non-intrusive) */}
          {showInstagramBanner && isInstagram && isMobile && !showHeavyVideoLoader && (
            <div
              className="absolute top-0 left-0 right-0 z-50 px-3"
              style={{ marginTop: "max(0.5rem, env(safe-area-inset-top, 6px))" }}
            >
              <div className="bg-black/40 backdrop-blur-sm rounded-full px-3.5 py-2 flex items-center gap-2 border border-white/[0.06]">
                <ExternalLink className="w-3.5 h-3.5 text-white/50 shrink-0" strokeWidth={1.5} />
                {linkCopiedForBrowser ? (
                  <>
                    <span className="text-white/70 text-[11px] flex-1 leading-tight truncate">
                      {t("pasteInBrowser", detectedLanguage)}
                    </span>
                    {isIOS && (
                      <a
                        href={typeof window !== "undefined" ? window.location.href : "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 text-[11px] underline underline-offset-2 shrink-0"
                      >
                        {t("longPressHint", detectedLanguage)}
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-white/65 text-[11px] flex-1 leading-tight truncate">
                      {t("betterExperience", detectedLanguage)}
                    </span>
                    <button
                      type="button"
                      onClick={handleOpenInBrowser}
                      className="text-white/80 text-[11px] font-medium shrink-0 underline underline-offset-2 active:text-white transition-colors"
                    >
                      {t("openInBrowser", detectedLanguage)}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleDismissInstagramBanner}
                  className="text-white/35 hover:text-white/65 shrink-0 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            </div>
          )}

          {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
          {showCTAButton && (
            <div
              className={`action-button-container ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                zIndex: 100,
                isolation: "isolate",
              }}
            >
              {muxFriendlyBanner && (
                <p
                  className="text-white/45 text-[10px] sm:text-[11px] text-center mb-1.5 px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                  role="note"
                >
                  {muxFriendlyBanner}
                </p>
              )}
              {ctaSubtitle && (
                <p className="cta-subtitle text-white/90 text-sm md:text-base text-center mb-2 drop-shadow-md">
                  {ctaSubtitle}
                </p>
              )}
              <button
                type="button"
                onClick={handleCTAClick}
                onTouchStart={handleCTATouchStart}
                onTouchEnd={handleCTATouchEnd}
                disabled={!showCTAButton || ctaBusy}
                className="cta-action-button"
                aria-label={ctaLabel}
                aria-disabled={!showCTAButton || ctaBusy}
                style={{ touchAction: "manipulation" }}
              >
                {ctaBusy ? (
                  <Loader2
                    className={`${
                      isMobile ? "w-4 h-4" : "w-5 h-5"
                    } shrink-0 animate-spin pointer-events-none`}
                    aria-hidden
                  />
                ) : (
                  showInstagramIcon && (
                    <Instagram
                      className={`${
                        isMobile ? "w-4 h-4" : "w-5 h-5"
                      } shrink-0 pointer-events-none`}
                      strokeWidth={2.5}
                      style={{
                        filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  )
                )}
                <span className="cta-button-text">{ctaLabel}</span>
                {!ctaBusy && (
                  <ExternalLink
                    className="cta-button-icon action-button-icon"
                    strokeWidth={2.5}
                  />
                )}
              </button>
              {showCTAButton &&
                isArgentinaPaymentContext(effectiveCountry, effectiveCountry) && (
                  <div
                    className="mt-2 flex justify-center rounded-lg bg-white/95 px-3 py-1.5 shadow-sm"
                    aria-label="Pago seguro con Mercado Pago"
                  >
                    <img
                      src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                      alt="Mercado Pago — pago seguro"
                      className="h-6 w-auto max-w-[min(100%,180px)] object-contain md:h-7"
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                )}
            </div>
          )}

          {/* Progressive image loading: LQIP → Full thumbnail (mobile-optimized) */}
          {awaitingFirstPlayback &&
            !parentOwnsPreview &&
            (placeholderUrl || thumbnailUrl) &&
            !thumbnailError && (
              <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                {/* LQIP - Blur-up effect */}
                {placeholderUrl && (
                  <div
                    className="absolute inset-0 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url(${placeholderUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      filter: "blur(20px)",
                      transform: "scale(1.1)",
                      opacity: thumbnailUrl ? 0.5 : 1,
                    }}
                  />
                )}
                {/* Full quality thumbnail */}
                {thumbnailUrl && (
                  <div
                    className="absolute inset-0 animate-fade-in"
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      animationDuration: "0.5s",
                      animationFillMode: "both",
                    }}
                  />
                )}
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
              </div>
            )}

          {renderMuxLoadChrome()}

          {/* Unmute affordance — center + high contrast (same as fullscreen path) */}
          {showMuteIndicator && isPlaying && !hideUnmutePrompt && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleMute();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleMute();
              }}
              className="absolute left-1/2 top-[42%] z-[60] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2.5 cursor-pointer touch-manipulation transition-opacity duration-300"
              style={{
                opacity: showMuteIndicator ? 1 : 0,
                WebkitTapHighlightColor: "transparent",
                minWidth: 96,
                minHeight: 96,
              }}
              aria-label={t("enableAudio", detectedLanguage)}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/35 bg-black/55 text-white shadow-[0_8px_28px_rgba(0,0,0,0.45)] backdrop-blur-md animate-pulse ring-2 ring-white/15">
                <svg
                  className="h-8 w-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.25}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                  <line x1="18" y1="6" x2="6" y2="18" strokeWidth={2.25} />
                </svg>
              </span>
              <span className="rounded-full border border-white/25 bg-black/60 px-3.5 py-1.5 text-[13px] font-semibold tracking-tight text-white shadow-md backdrop-blur-md">
                {t("tapForSound", detectedLanguage)}
              </span>
            </button>
          )}

          {!isPlaying && showPlayButton && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 hover:bg-white active:bg-white/90 flex items-center justify-center shadow-xl ui-transition duration-200 hover:scale-105 active:scale-95 touch-manipulation select-none border border-black/10"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  WebkitTouchCallout: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  cursor: "pointer",
                  minWidth: "96px",
                  minHeight: "96px",
                }}
                aria-label={t("tapToPlayWithSound", detectedLanguage)}
              >
                <Play
                  className="w-12 h-12 md:w-14 md:h-14 text-white ml-1.5"
                  fill="currentColor"
                  strokeWidth={2}
                />
              </button>
              {!hideUnmutePrompt && (
                <div className="bg-transparent backdrop-blur-md text-white/85 px-3 py-1.5 rounded-full text-[11px] font-medium flex items-center gap-1.5 border border-white/20">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                  <span className="tracking-tight">
                    {detectedLanguage === "en"
                      ? "🔊 Tap to play with sound"
                      : "🔊 Toca para reproducir con audio"}
                  </span>
                </div>
              )}
            </div>
          )}

          {onClose && (
            <button
              ref={closeButtonRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`mux-cta-close-btn absolute z-50 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors touch-manipulation outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              style={{
                top: "max(1rem, env(safe-area-inset-top))",
                right: "max(1rem, env(safe-area-inset-right))",
              }}
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 shrink-0" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}
      {mpIgEscapeOverlay}
    </>
  );
};

export default MuxVideoPlayerWithCTA;
